// Bilingual blog content. Each post is rendered at /blog/:slug (Turkish) and
// /en/blog/:slug (English). Keep slugs stable - they are part of the URL and
// are listed in package.json "reactSnap.include" and public/sitemap.xml.

export type BlogLang = "tr" | "en";

// A run of paragraph text. When `to` is set the segment renders as an internal
// react-router link (used for contextual in-body links to the pillar guides and
// landing pages); otherwise it is plain text. Keeps body copy declarative while
// still funnelling readers into the topic cluster.
export interface BlogTextSegment {
  text: string;
  to?: string;
}

/** A data table inside an article. Rendered as a real <table> with a caption,
 * scrollable on narrow screens. Keep cells short plain text. */
export interface BlogTable {
  caption: string;
  headers: string[];
  rows: string[][];
}

/**
 * One block of article body.
 * - heading / paragraph / list: as before (`list` is a bulleted list).
 * - orderedList: numbered steps (`items`).
 * - table: a captioned data table (`table`).
 * - callout: a labelled box (`title` + `text` or `segments`). `tone` picks the
 *   style: "answer" for the direct answer near the top, "example" for a worked
 *   example (say "illustrative" in the copy unless it is approved evidence),
 *   "limitation" for exceptions and manual-review cases, "product" for what
 *   Shift Tracker itself does (only behaviour stated on the product pages).
 */
export interface BlogBlock {
  type: "heading" | "paragraph" | "list" | "orderedList" | "table" | "callout";
  text?: string;
  // Rich paragraph alternative to `text`: an ordered list of plain-text and
  // internal-link segments. Read for `paragraph` and `callout`.
  segments?: BlogTextSegment[];
  items?: string[];
  table?: BlogTable;
  tone?: "answer" | "example" | "limitation" | "product";
  title?: string;
}

/** An ordinary source link listed under the article ("Sources"). */
export interface BlogSource {
  label: string;
  url: string; // absolute https:// URL, or an internal trailing-slash path
}

export interface BlogLocale {
  // Visible article heading (h1), blog card and related-post title.
  title: string;
  /**
   * Optional full document <title>, INCLUDING the " - Shift Tracker" suffix.
   * Set it when `${title} - Shift Tracker` would exceed 60 characters, so the
   * search-result title stays short (35-60 chars, main search term first)
   * without shortening the visible h1. When omitted, BlogPost renders
   * `${title} - Shift Tracker`.
   */
  seoTitle?: string;
  // Used as the meta description and the card excerpt on the blog index.
  // Keep it 120-155 characters (never below 70 or above 160).
  description: string;
  /**
   * ISO date (YYYY-MM-DD) of the last genuine content change of THIS language
   * version. Set it only when the article's substance changes (not for a
   * deploy, a typo or a meta tweak), and bump the URL's <lastmod> in
   * public/sitemap.xml to at least this date. Omitted = unchanged since
   * publication. Never invent a historical date.
   */
  modifiedAt?: string;
  body: BlogBlock[];
  // Optional "Sources" list rendered under the article.
  sources?: BlogSource[];
}

export interface BlogPost {
  slug: string; // Turkish slug, used at /blog/<slug>/
  enSlug: string; // English slug, used at /en/blog/<enSlug>/
  // ISO date (YYYY-MM-DD) the article was first published. Never changes.
  publishedAt: string;
  /**
   * Name of a real person who reviewed the article for accuracy, only when
   * one exists. The author is always the Shift Tracker organization.
   */
  reviewedBy?: string;
  tr: BlogLocale;
  en: BlogLocale;
}

export const blogPosts: BlogPost[] = [
  {
    slug: "akaryakit-istasyonu-nakit-akis-yonetimi",
    enSlug: "fuel-station-cash-flow-management",
    publishedAt: "2026-05-20",
    tr: {
      title: "Akaryakıt İstasyonu Nakit Akış Yönetimi: Kapsamlı Rehber",
      seoTitle: "Akaryakıt İstasyonu Nakit Akış Yönetimi - Shift Tracker",
      description:
        "Akaryakıt istasyonlarında nakit akışını kontrol altında tutmanın yolları: vardiya mutabakatı, banka eşleştirmesi ve denetlenebilir kayıtlar.",
      body: [
        {
          type: "paragraph",
          text: "Akaryakıt ve benzin istasyonları, gün içinde çok sayıda nakit ve kredi kartı işlemini aynı anda yönetir. Bu yoğunlukta nakit akışını kontrol altında tutmak, hem kârlılığı korumak hem de personel açıklarını erken fark etmek için kritiktir. Bu rehberde, istasyon nakit akışı yönetiminin temel adımlarını ele alıyoruz.",
        },
        {
          type: "heading",
          text: "Nakit akışı neden vardiya bazında izlenmeli?",
        },
        {
          type: "paragraph",
          text: "Bir istasyonda gün, genellikle birden fazla vardiyaya bölünür. Nakit akışını gün sonunda toplu olarak değil, her vardiya kapanışında izlemek, farkların hangi vardiyada ve hangi personelde oluştuğunu netleştirir. Vardiya bazlı izleme, sorunların kaynağını saatler sonra değil, anında ortaya çıkarır.",
        },
        {
          type: "heading",
          text: "Otomasyon satışlarını banka kayıtlarıyla eşleştirin",
        },
        {
          type: "paragraph",
          text: "Kredi kartı satışlarının gerçekten bankaya geçip geçmediğini doğrulamanın tek güvenilir yolu, ERP/otomasyon satışlarını banka kayıtlarıyla karşılaştırmaktır. Banka onaylı tutarlar, açıklayıcı nakit tutarlardan ayrı tutulduğunda her kuruşun izi sürülebilir hale gelir.",
        },
        {
          type: "list",
          items: [
            "Banka tarafından onaylanan kart tutarlarını ayrı izleyin.",
            "Nakit olarak girilmiş ancak kartla ödenmiş satışları düzeltin.",
            "Çözülmemiş farkları öne çıkarın ve sorumlu vardiyaya bağlayın.",
          ],
        },
        {
          type: "heading",
          text: "Personel açıklarını otomatik tespit edin",
        },
        {
          type: "paragraph",
          text: "Her vardiya banka onaylı ödemeler ve ERP toplamlarıyla mutabık kılındığında, oluşan farklar otomatik olarak ilgili vardiyaya ve personele atanabilir. Gerekçe, inceleyen ve zaman damgası kayıt altına alındığında açık yönetimi denetlenebilir bir sürece dönüşür.",
        },
        {
          type: "heading",
          text: "Manuel işten otomasyona geçiş",
        },
        {
          type: "paragraph",
          text: "Kredi kartı satışları banka onaylı kayıtlardan otomatik eşleştirilip onaylandığında, geriye yalnızca bankaya yatan nakdi doğrulamak kalır. Bu yaklaşım, daha önce 1,5-2 saat süren vardiya incelemesini saniyelere indirir ve manuel iş yükünü belirgin biçimde azaltır.",
        },
        {
          type: "paragraph",
          text: "Shift Tracker, akaryakıt ve benzin istasyonları için bu akışı uçtan uca otomatikleştirir. Demo talep etmek için bizimle iletişime geçebilirsiniz.",
        },
      ],
    },
    en: {
      title: "Fuel Station Cash Flow Management: A Complete Guide",
      seoTitle: "Fuel Station Cash Flow Management Guide - Shift Tracker",
      description:
        "How to keep fuel station cash flow under control: track it shift by shift, match ERP sales with bank records, and catch employee deficits early.",
      body: [
        {
          type: "paragraph",
          text: "Fuel and gas stations handle a high volume of cash and credit card transactions at the same time. Keeping cash flow under control at this pace is critical both to protect profitability and to catch employee deficits early. This guide walks through the core steps of station cash flow management.",
        },
        { type: "heading", text: "Why track cash flow shift by shift?" },
        {
          type: "paragraph",
          text: "A station's day is usually split into several shifts. Tracking cash flow at every shift close, rather than once at the end of the day, clarifies which shift and which employee a discrepancy came from. Shift-level tracking surfaces problems immediately instead of hours later.",
        },
        { type: "heading", text: "Match ERP sales with bank records" },
        {
          type: "paragraph",
          text: "The only reliable way to confirm that card sales actually reached the bank is to compare ERP/automation sales against bank records. When bank-confirmed amounts are kept separate from descriptive cash amounts, every amount becomes traceable.",
        },
        {
          type: "list",
          items: [
            "Track bank-confirmed card amounts separately.",
            "Correct sales entered as cash that were actually paid by card.",
            "Surface unresolved differences and tie them to the responsible shift.",
          ],
        },
        { type: "heading", text: "Detect employee deficits automatically" },
        {
          type: "paragraph",
          text: "When each shift is reconciled against bank-confirmed payments and ERP totals, the resulting differences can be attributed automatically to the relevant shift and employee. Recording the reason, reviewer, and timestamp turns deficit handling into an auditable process.",
        },
        { type: "heading", text: "From manual work to automation" },
        {
          type: "paragraph",
          text: "When card sales are matched and approved automatically from bank-confirmed records, all that remains is verifying the cash deposited to the bank. This approach cuts a shift review that used to take 1.5 to 2 hours down to seconds and noticeably lowers manual workload.",
        },
        {
          type: "paragraph",
          text: "Shift Tracker automates this flow end to end for fuel and gas stations. Contact us to request a demo.",
        },
      ],
    },
  },
  {
    slug: "benzin-istasyonu-yazilimi-secimi",
    enSlug: "how-to-choose-gas-station-software",
    publishedAt: "2026-05-24",
    tr: {
      title: "Benzin İstasyonu Yazılımı Seçerken Nelere Dikkat Etmeli?",
      seoTitle: "Benzin İstasyonu Yazılımı Nasıl Seçilir? - Shift Tracker",
      description:
        "Benzin istasyonu yazılımı seçerken değerlendirmeniz gereken kriterler: otomasyon entegrasyonu, mutabakat, raporlama ve denetlenebilirlik.",
      body: [
        {
          type: "paragraph",
          text: "Doğru benzin istasyonu yazılımı, günlük operasyonu kolaylaştırmanın ötesinde nakit akışınızı koruyan bir kontrol katmanıdır. Yanlış seçim ise manuel iş yükünü artırır ve farkların gözden kaçmasına yol açar. İşte değerlendirmeniz gereken temel kriterler.",
        },
        { type: "heading", text: "1. Otomasyon ve ERP entegrasyonu" },
        {
          type: "paragraph",
          text: "Yazılımın TURPAK gibi pompa otomasyon sistemleriyle ve kullandığınız ERP ile sorunsuz entegre olması gerekir. Satış verisi otomatik aktığında, manuel veri girişi kaynaklı hatalar ortadan kalkar.",
        },
        { type: "heading", text: "2. Banka mutabakatı yeteneği" },
        {
          type: "paragraph",
          text: "İyi bir yazılım, kredi kartı satışlarını banka kayıtlarıyla eşleştirip onaylayabilmelidir. Banka onaylı tutarların açıklayıcı nakit tutarlardan ayrılması, mutabakatı güvenilir kılar.",
        },
        { type: "heading", text: "3. Denetlenebilir kayıtlar" },
        {
          type: "paragraph",
          text: "Yapılan her düzeltmenin gerekçesi, inceleyeni ve zaman damgası saklanmalıdır. Denetlenebilirlik, hem iç kontrol hem de olası anlaşmazlıklarda kanıt sağlar.",
        },
        { type: "heading", text: "4. Aradığınız kriterleri özetleyen kontrol listesi" },
        {
          type: "list",
          items: [
            "Pompa otomasyonu ve ERP ile gerçek zamanlı entegrasyon",
            "Vardiya bazlı mutabakat ve personel açığı takibi",
            "Banka onaylı kart eşleştirmesi ve ödeme tipi düzeltmesi",
            "Çok istasyonlu yapı ve rol bazlı erişim",
            "Tarayıcıdan erişilebilen, kurulum gerektirmeyen web yazılımı",
          ],
        },
        {
          type: "paragraph",
          text: "Shift Tracker, bu kriterlerin tamamını tek bir akışta sunar. İhtiyaçlarınızı konuşmak için demo talep edebilirsiniz.",
        },
      ],
    },
    en: {
      title: "How to Choose Gas Station Software",
      description:
        "Key criteria for choosing gas station software: automation integration, reconciliation, reporting, and auditability.",
      body: [
        {
          type: "paragraph",
          text: "The right gas station software is more than a way to simplify daily operations; it is a control layer that protects your cash flow. The wrong choice increases manual workload and lets discrepancies slip through. Here are the core criteria to evaluate.",
        },
        { type: "heading", text: "1. Automation and ERP integration" },
        {
          type: "paragraph",
          text: "The software should integrate cleanly with pump automation systems such as TURPAK and with the ERP you use. When sales data flows in automatically, errors from manual data entry disappear.",
        },
        { type: "heading", text: "2. Bank reconciliation capability" },
        {
          type: "paragraph",
          text: "Good software should match and approve credit card sales against bank records. Separating bank-confirmed amounts from descriptive cash amounts makes reconciliation trustworthy.",
        },
        { type: "heading", text: "3. Audit-ready records" },
        {
          type: "paragraph",
          text: "Every correction should store its reason, reviewer, and timestamp. Auditability provides evidence for both internal control and any future disputes.",
        },
        { type: "heading", text: "4. A checklist of what to look for" },
        {
          type: "list",
          items: [
            "Real-time integration with pump automation and ERP",
            "Shift-level reconciliation and employee deficit tracking",
            "Bank-confirmed card matching and payment type correction",
            "Multi-station support with role-based access",
            "Browser-based software that needs no installation",
          ],
        },
        {
          type: "paragraph",
          text: "Shift Tracker delivers all of these in a single flow. Request a demo to discuss your needs.",
        },
      ],
    },
  },
  {
    slug: "vardiya-mutabakati-ve-personel-acigi",
    enSlug: "shift-reconciliation-employee-deficits",
    publishedAt: "2026-05-28",
    tr: {
      title: "Vardiya Mutabakatı ve Personel Açığı Yönetimi",
      seoTitle: "Vardiya Mutabakatı ve Personel Açığı Takibi - Shift Tracker",
      description:
        "Akaryakıt istasyonlarında vardiya mutabakatının nasıl yapıldığı ve personel açıklarının denetlenebilir şekilde nasıl yönetileceği.",
      modifiedAt: "2026-11-04",
      body: [
        {
          type: "paragraph",
          text: "Vardiya mutabakatı, bir vardiyada gerçekleşen satışların, tahsil edilen nakit ve kartların ve bankaya geçen tutarların birbiriyle tutarlı olduğunu doğrulama sürecidir. Doğru yapıldığında, personel açıkları tahmine değil kanıta dayanır.",
        },
        {
          type: "callout",
          tone: "answer",
          title: "Kısa cevap",
          text: "Vardiya mutabakatı, otomasyondaki vardiya satışlarını ödeme tipine göre ayırıp kart tarafını bankanın POS kayıtlarıyla, nakit tarafını sayılan ve bankaya yatırılan nakitle karşılaştırmaktır. Personel açığı, ödeme tipi hataları, zamanlama farkları ve komşu vardiya kayıtları açıklandıktan sonra kalan farktır; gerekçesi ve inceleyeni kayda geçirilerek ilgili vardiyaya ve personele atanır.",
        },
        { type: "heading", text: "Vardiya mutabakatı ve personel açığı nedir?" },
        {
          type: "paragraph",
          text: "Vardiya mutabakatı, tek bir vardiyanın (belirli bir başlangıç ve bitiş saati ile sorumlu personel) satışını o satışın tahsilatıyla eşleştirir. Kartla ödenen kısım bankanın kaydıyla, nakit kısmı ise sayılan, teslim edilen veya bankaya yatırılan nakitle doğrulanır. Personel açığı (kasa açığı) ise bu karşılaştırmadan sonra hâlâ açıklanamayan, çoğunlukla nakit tarafında ortaya çıkan eksikliktir.",
        },
        {
          type: "paragraph",
          text: "Her fark bir açık değildir. Mutabakat sonunda ortaya çıkan farkın bir veri giriş hatası mı, bir ödeme tipi yanlışlığı mı yoksa gerçek bir açık mı olduğu, denetlenebilir kayıtlar sayesinde netleşir. Mutabakat için gereken girdiler şunlardır:",
        },
        {
          type: "list",
          items: [
            "Otomasyon vardiya raporu: vardiyanın satışları, ödeme tipi kırılımı ve sorumlu personel (TURPAK, Asis gibi sistemlerden).",
            "POS gün sonu raporu: cihazın kendi kart toplamı; destekleyici bir belgedir, tek başına kanıt değildir.",
            "Banka kayıtları: işlem bazlı POS hareketleri veya üye işyeri ekstresi ile hesap hareketleri.",
            "Nakit belgeleri: vardiya devrinde sayılan nakit ve bankaya yatırma dekontları.",
            "Varsa diğer ödeme tipleri: filo, taşıt tanıma veya veresiye satışların kendi listeleri.",
          ],
        },
        { type: "heading", text: "Adım adım vardiya mutabakatı" },
        {
          type: "orderedList",
          items: [
            "Vardiyanın sınırlarını sabitleyin: başlangıç ve bitiş saatini, sorumlu personeli ve otomasyondaki satış toplamlarını ödeme tipine göre alın.",
            "Kart satışlarını bankanın işlem bazlı POS kayıtlarıyla eşleştirin: aynı tutar, aynı terminal ve yakın saat. Her banka kaydı yalnızca bir satışa bağlanmalıdır.",
            "Yanlış girilmiş ödeme tiplerini düzeltin: bankada karşılığı olan ama otomasyona nakit girilmiş bir satışı karta çevirin ve orijinal kaydı saklayın.",
            "Beklenen nakdi yeniden hesaplayın ve sayılan, teslim edilen veya bankaya yatırılan nakitle karşılaştırın.",
            "Zamanlama ve sınır farklarını ayırın: bankaya henüz yansımamış işlemler, komşu vardiyaya ait ödemeler, iade ve iptaller.",
            "Kalan farkı gerekçesiyle ilgili vardiyaya ve personele atayın; inceleyeni ve zamanı kaydedin.",
            "Vardiyayı kapatın ve kaydı sonradan değiştirilemeyecek şekilde saklayın.",
          ],
        },
        { type: "heading", text: "Örnek: 600 TL'lik fark nasıl 150 TL'ye iner?" },
        {
          type: "callout",
          tone: "example",
          title: "Örnek (temsili)",
          text: "Tutarlar yalnızca yöntemi göstermek için seçilmiştir. Otomasyon bir vardiyada 14.000 TL kart ve 6.000 TL nakit satış gösteriyor; bankanın POS kayıtlarında bu vardiyaya ait 14.450 TL kart işlemi var ve bankaya 5.400 TL nakit yatırılmış. İlk bakışta 600 TL nakit açığı var gibi görünür. İnceleme, bankadaki 450 TL'lik bir POS işleminin otomasyona nakit girilmiş bir satışa ait olduğunu gösterir. Bu satış karta düzeltildiğinde kart tarafı bankayla tutar, beklenen nakit 5.550 TL'ye iner ve açıklanamayan fark 150 TL olur. Gerekçesiyle personele atanacak tutar 600 TL değil, 150 TL'dir.",
        },
        {
          type: "table",
          table: {
            caption: "Temsili vardiya: düzeltme öncesi ve sonrası (tutarlar örnektir)",
            headers: ["Kalem", "Düzeltme öncesi", "Düzeltme sonrası"],
            rows: [
              ["Kart satışı (otomasyon)", "14.000 TL", "14.450 TL"],
              ["Kart işlemleri (banka POS)", "14.450 TL", "14.450 TL"],
              ["Kart farkı", "450 TL", "0 TL"],
              ["Beklenen nakit", "6.000 TL", "5.550 TL"],
              ["Yatırılan nakit (dekont)", "5.400 TL", "5.400 TL"],
              ["Nakit farkı", "600 TL eksik", "150 TL eksik"],
            ],
          },
        },
        {
          type: "paragraph",
          segments: [
            { text: "Örnekte bankanın brüt işlem tutarları kullanılır. Komisyon ve valör, hesaba geçen net tutarı değiştirir ama satış bazında eşleştirmeyi değiştirmez; bunu " },
            { text: "kredi kartı satışının bankaya ne zaman geçtiğini", to: "/blog/kredi-karti-satisi-bankaya-ne-zaman-gecer/" },
            { text: " anlatan rehberde ayrıntılı ele aldık." },
          ],
        },
        { type: "heading", text: "İstisnalar ve elle inceleme gereken durumlar" },
        {
          type: "list",
          items: [
            "Bankaya henüz yansımamış kart işlemleri: banka kaydı gelmeden, eşleşmeyen bir kart satışını açık saymayın.",
            "Komşu vardiya kayıtları: devir saatine yakın bir ödeme, ait olduğu vardiyadan farklı bir vardiyada görünebilir.",
            "Kısmi ödemeler: bir kısmı kartla, kalanı nakit ödenen satışta iki kısım ayrı izlenmelidir.",
            "Aynı tutarda birden fazla işlem: eşleşme belirsizse saat ve terminal bilgisiyle elle karar verilmelidir.",
            "İade, iptal ve sonradan gelen banka düzeltmeleri: farkın hangi vardiyaya ait olduğunu değiştirebilir.",
            "Banka POS'u dışındaki ödeme tipleri: filo veya veresiye satışlar banka kayıtlarında görünmeyebilir; kendi belgeleriyle kontrol edilmelidir.",
          ],
        },
        {
          type: "callout",
          tone: "limitation",
          title: "Dikkat edilmesi gerekenler",
          text: "Bir farkın personele atanması, açığın kaydıdır; personelin sorumluluğuna veya ücretten kesintiye ilişkin kararlar iş hukuku kurallarına tabidir, bu yüzden böyle bir karardan önce kayıtları bir uzmanla değerlendirin. Tek seferlik bir fark kasıt kanıtı da değildir; tekrarlayan örüntüler ayrıca incelenmelidir.",
        },
        { type: "heading", text: "Shift Tracker'da vardiya mutabakatı" },
        // Sources: landingPages.ts:130-135 (otomatik aktarım; aynı tutar, aynı terminal, dar zaman
        // aralığı, tek hareket; nakit yatırma referansı dekontta; eşleşti / inceleme / açık),
        // landingPages.ts:146 (ödeme tipi düzeltmesi, orijinal tip saklanır), languageData.tsx:4126
        // (tr.home.faq: banka onaylı ödemeler ve ERP toplamlarıyla mutabakat; farklar gerekçe,
        // inceleyen ve zaman damgasıyla vardiyaya ve personele atanır), languageData.tsx:3984 ve 3989
        // (tr.home.features: banka onaylı / açıklayıcı nakit / çözümlenmemiş ayrı; yakın vardiya
        // ödemeleri işaretli), blogPosts.ts vardiyayi-bir-dakikada-nasil-kapatirsiniz tr "Adım 5"
        // (kilitli vardiya değiştirilemez)
        {
          type: "callout",
          tone: "product",
          title: "Shift Tracker bunu nasıl yapar?",
          segments: [
            { text: "Shift Tracker satışları TURPAK veya Asis otomasyonundan otomatik alır ve her vardiyayı banka onaylı ödemeler ve ERP toplamlarıyla mutabık kılar. Kart satışları aynı tutar, aynı terminal ve dar bir zaman aralığıyla tek bir banka hareketine eşleştirilir; nakit girilmiş ama kartla ödenmiş satışlar orijinal kayıt korunarak düzeltilir; nakit yatırmalar ise dekonta yazılan benzersiz bir referansla banka ekstresinde doğrulanır. Banka onaylı kısım, açıklayıcı nakit kısmı ve çözümlenmemiş tutar ayrı izlenir; yakın vardiyalara ait ödemeler açıkça işaretlenir. Her satır eşleşti, inceleme gerekiyor veya açık durumundadır; kalan fark gerekçe, inceleyen ve zaman damgasıyla ilgili vardiyaya ve personele atanır ve kilitlenen vardiya değiştirilemez. Ayrıntılar için " },
            { text: "vardiya alma programı", to: "/vardiya-alma-programi/" },
            { text: " ve " },
            { text: "nakit yatırma doğrulama", to: "/nakit-yatirma-dogrulama/" },
            { text: " sayfalarına bakın." },
          ],
        },
        { type: "heading", text: "Şeffaflık güveni artırır" },
        {
          type: "paragraph",
          text: "Her düzeltmenin gerekçesi ve sorumlusu kayıt altında olduğunda, personel açığı tartışmaları objektif bir zemine oturur. Bu şeffaflık hem yönetim hem de saha ekibi için güven sağlar: dürüst bir hata hata olarak, gerçek bir açık da açık olarak görünür.",
        },
        {
          type: "paragraph",
          segments: [
            { text: "Vardiya kapanışının günlük pratikte nasıl göründüğünü " },
            { text: "bir vardiyayı bir dakikanın altında kapatma", to: "/blog/vardiyayi-bir-dakikada-nasil-kapatirsiniz/" },
            { text: " yazısında adım adım anlattık. Shift Tracker, vardiya mutabakatını ve personel açığı takibini tek akışta birleştirir; detaylar için bizimle iletişime geçin." },
          ],
        },
      ],
    },
    en: {
      title: "Shift Reconciliation and Employee Deficit Management",
      seoTitle: "Shift Reconciliation and Employee Deficits - Shift Tracker",
      description:
        "How shift reconciliation works at fuel stations, step by step, and how to manage employee deficits in an auditable way, based on evidence, not guesswork.",
      modifiedAt: "2026-11-04",
      body: [
        {
          type: "paragraph",
          text: "Shift reconciliation is the process of confirming that the sales made during a shift, the cash and cards collected, and the amounts that reached the bank are all consistent with each other. Done correctly, employee deficits rest on evidence rather than guesswork.",
        },
        {
          type: "callout",
          tone: "answer",
          title: "Short answer",
          text: "Shift reconciliation splits a shift's automation sales by payment type, then checks the card side against the bank's POS records and the cash side against the cash counted and deposited. An employee deficit is what remains once payment type errors, timing differences and neighboring-shift items are explained; it is attributed to the relevant shift and employee with its reason and reviewer on record.",
        },
        { type: "heading", text: "What shift reconciliation and an employee deficit are" },
        {
          type: "paragraph",
          text: "Shift reconciliation ties one shift (a set start and end time and the employee responsible) to what was collected for it. The card portion is confirmed against the bank's record, and the cash portion against the cash counted, handed over or deposited at the bank. An employee deficit (a cash shortage) is the shortfall, usually on the cash side, that is still unexplained after that comparison.",
        },
        {
          type: "paragraph",
          text: "Not every difference is a deficit. Audit-ready records clarify whether a difference is a data entry error, a payment type mistake, or a genuine deficit. These are the inputs you need:",
        },
        {
          type: "list",
          items: [
            "Automation shift report: the shift's sales, split by payment type, and the employee responsible (from systems such as TURPAK or Asis).",
            "POS end-of-day report: the device's own card total; supporting evidence, not proof on its own.",
            "Bank records: transaction-level POS records or the merchant statement, plus the account movements.",
            "Cash documents: the cash counted at handover and the bank deposit receipts.",
            "Other payment types, if any: separate lists for fleet, vehicle-recognition or on-account sales.",
          ],
        },
        { type: "heading", text: "Shift reconciliation, step by step" },
        {
          type: "orderedList",
          items: [
            "Fix the shift boundaries: take the start and end time, the employee responsible and the automation's sales totals by payment type.",
            "Match card sales to the bank's transaction-level POS records: the same amount, the same terminal and a close time. Each bank record should be tied to only one sale.",
            "Correct misclassified payment types: switch a sale entered as cash to card when the bank holds its POS transaction, and keep the original entry.",
            "Recalculate the expected cash and compare it with the cash counted, handed over or deposited.",
            "Separate timing and boundary differences: transactions the bank has not reported yet, payments that belong to a neighboring shift, refunds and cancellations.",
            "Attribute the remaining difference to the relevant shift and employee with a reason, and record the reviewer and the time.",
            "Close the shift and keep the record so it cannot be changed afterwards.",
          ],
        },
        { type: "heading", text: "Example: how a TRY 600 gap becomes TRY 150" },
        {
          type: "callout",
          tone: "example",
          title: "Example (illustrative)",
          text: "The amounts are chosen only to show the method. The automation shows TRY 14,000 in card sales and TRY 6,000 in cash for a shift; the bank's POS records hold TRY 14,450 in card transactions for that shift, and TRY 5,400 in cash was deposited. At first glance cash is TRY 600 short. On review, a TRY 450 POS transaction at the bank turns out to belong to a sale entered as cash in the automation. Once that sale is corrected to card, the card side agrees with the bank, the expected cash drops to TRY 5,550 and the unexplained difference is TRY 150. The amount to attribute to the employee, with its reason, is TRY 150, not TRY 600.",
        },
        {
          type: "table",
          table: {
            caption: "Illustrative shift, before and after correction (example amounts)",
            headers: ["Item", "Before correction", "After correction"],
            rows: [
              ["Card sales (automation)", "TRY 14,000", "TRY 14,450"],
              ["Card transactions (bank POS)", "TRY 14,450", "TRY 14,450"],
              ["Card difference", "TRY 450", "TRY 0"],
              ["Expected cash", "TRY 6,000", "TRY 5,550"],
              ["Cash deposited (receipt)", "TRY 5,400", "TRY 5,400"],
              ["Cash difference", "TRY 600 short", "TRY 150 short"],
            ],
          },
        },
        {
          type: "paragraph",
          segments: [
            { text: "The example uses the bank's gross transaction amounts. Commission and the value date change the net amount credited to the account, not the sale-level match; we cover that in our guide to " },
            { text: "when card sales reach the bank", to: "/en/blog/when-do-card-sales-reach-the-bank/" },
            { text: "." },
          ],
        },
        { type: "heading", text: "Exceptions and when to review by hand" },
        {
          type: "list",
          items: [
            "Card transactions the bank has not reported yet: do not treat an unmatched card sale as a deficit before the bank record arrives.",
            "Neighboring-shift items: a payment close to handover can show up in a shift it does not belong to.",
            "Partial payments: when part of a sale is paid by card and the rest in cash, track the two portions separately.",
            "Several transactions with the same amount: when the match is ambiguous, decide by hand using the time and the terminal.",
            "Refunds, cancellations and later bank adjustments: these can change which shift a difference belongs to.",
            "Payment types outside the bank POS: fleet or on-account sales may not appear in bank records and need their own documents.",
          ],
        },
        {
          type: "callout",
          tone: "limitation",
          title: "Keep in mind",
          text: "Attributing a difference to an employee records the deficit; decisions about the employee's liability or any deduction from pay are governed by employment law, so review the records with an expert before making one. A single difference is not proof of intent either; repeated patterns deserve a separate look.",
        },
        { type: "heading", text: "Shift reconciliation in Shift Tracker" },
        // Sources: landingPages.ts:280-285 (automatic import; same amount, same terminal, narrow
        // time window, one transaction; deposit reference on the slip; matched / needs review /
        // deficit), landingPages.ts:296 (payment type correction keeps the original type),
        // languageData.tsx:1563 (eng.home.faq: reconciled against bank-confirmed payments and ERP
        // totals; differences attributed with reason, reviewer and timestamp), languageData.tsx:1421
        // and 1426 (eng.home.features: bank-confirmed / descriptive cash / unresolved tracked
        // separately; nearby-shift payments clearly marked), blogPosts.ts
        // how-to-close-a-shift-in-one-minute en "Step 5" (a locked shift cannot be altered)
        {
          type: "callout",
          tone: "product",
          title: "What Shift Tracker does",
          segments: [
            { text: "Shift Tracker pulls sales automatically from your TURPAK or Asis automation and reconciles every shift against bank-confirmed payments and ERP totals. Card sales are matched to a single bank transaction with the same amount, the same terminal and a narrow time window; sales entered as cash but paid by card are corrected while the original entry is kept; and cash deposits are verified on the bank statement through a unique reference written on the deposit slip. Bank-confirmed portions, descriptive cash portions and unresolved amounts are tracked separately, and payments that belong to nearby shifts are clearly marked. Every row is matched, needs review or deficit; the remaining difference is attributed to the relevant shift and employee with its reason, reviewer and timestamp, and a locked shift cannot be altered. See the " },
            { text: "shift management program", to: "/en/shift-management-program/" },
            { text: " and " },
            { text: "deposit verification", to: "/en/deposit-verification/" },
            { text: " pages for details." },
          ],
        },
        { type: "heading", text: "Transparency builds trust" },
        {
          type: "paragraph",
          text: "When the reason and owner of every correction are on record, deficit discussions rest on an objective basis. This transparency builds trust for both management and the field team: an honest mistake shows up as a mistake, and a genuine deficit shows up as a deficit.",
        },
        {
          type: "paragraph",
          segments: [
            { text: "We walk through what a shift close looks like day to day in " },
            { text: "how to close a shift in under a minute", to: "/en/blog/how-to-close-a-shift-in-one-minute/" },
            { text: ". Shift Tracker combines shift reconciliation and employee deficit tracking in a single flow; contact us for details." },
          ],
        },
      ],
    },
  },
  {
    slug: "turpak-otomasyonu-banka-mutabakati",
    enSlug: "turpak-automation-vs-bank-reconciliation",
    publishedAt: "2026-06-02",
    tr: {
      title: "TURPAK Otomasyonunuz Var, Peki Banka Mutabakatı?",
      seoTitle: "TURPAK Otomasyonu ve Banka Mutabakatı - Shift Tracker",
      description:
        "Pompa otomasyonu satışı kaydeder ama paranın bankaya geçtiğini kanıtlamaz. TURPAK verisini banka onaylı kayıtlarla eşleştirerek mutabakat açığını kapatın.",
      body: [
        {
          type: "paragraph",
          text: "TURPAK, Asis ve benzeri pompa otomasyon sistemleri akaryakıt istasyonlarında satışın pompada doğru ölçülmesini ve kaydedilmesini sağlar. Ancak bir satışın otomasyonda görünmesi, o paranın gerçekten bankaya geçtiği anlamına gelmez. Mutabakat açığı tam burada başlar.",
        },
        { type: "heading", text: "Otomasyonun yaptığı ve yapmadığı" },
        {
          type: "paragraph",
          text: "Pompa ve tank otomasyonu; litre, stok, vardiya ve pompa-tank verisini üretmekte güçlüdür. Buna karşılık kredi kartı satışlarının bankaya yansıyıp yansımadığını, nakit girişlerinin doğruluğunu ve ödeme tiplerinin gerçeğe uygunluğunu banka kayıtlarıyla karşılaştırmaz.",
        },
        { type: "heading", text: "Mutabakat açığı nerede oluşur?" },
        {
          type: "list",
          items: [
            "Otomasyonda görünen bir kart satışı bankaya hiç geçmemiş olabilir.",
            "Nakit olarak girilen bir satış aslında kartla ödenmiş olabilir.",
            "Vardiya kapanışı pompa toplamına dayanır; banka onayına değil.",
          ],
        },
        { type: "heading", text: "Banka onaylı mutabakat ne ekler?" },
        {
          type: "paragraph",
          text: "Shift Tracker, otomasyondan gelen satış verisini banka onaylı ödemelerle yan yana eşleştirir. Yanlış girilen ödeme tiplerini düzeltir, banka onaylı tutarları açıklayıcı nakit tutarlardan ayırır ve her düzeltmeyi gerekçe, inceleyen ve zaman damgasıyla kaydeder.",
        },
        { type: "heading", text: "Otomasyon ve mutabakat birlikte" },
        {
          type: "paragraph",
          text: "Pompa otomasyonu satışı ölçer; Shift Tracker o satışın bankaya geçtiğini kanıtlar. İkisi birlikte, vardiya açıklarını ve suistimalleri görünür kılan eksiksiz bir kontrol oluşturur. Demo için bizimle iletişime geçin.",
        },
      ],
    },
    en: {
      title: "You Have TURPAK Automation, But What About Bank Reconciliation?",
      seoTitle: "TURPAK Automation vs Bank Reconciliation - Shift Tracker",
      description:
        "Pump automation records sales but doesn't prove the money reached the bank. Match TURPAK data with bank-confirmed records to close the reconciliation gap.",
      body: [
        {
          type: "paragraph",
          text: "Pump automation systems like TURPAK and Asis make sure a sale is measured and recorded correctly at the pump. But a sale appearing in automation does not mean the money actually reached the bank. That is exactly where the reconciliation gap begins.",
        },
        { type: "heading", text: "What automation does, and doesn't do" },
        {
          type: "paragraph",
          text: "Pump and tank automation is strong at producing litre, inventory, shift and pump-tank data. What it does not do is compare your card sales against the bank to confirm they settled, check that cash figures are accurate, or verify that the reported payment types are truthful. Those checks need bank records.",
        },
        { type: "heading", text: "Where the reconciliation gap appears" },
        {
          type: "list",
          items: [
            "A card sale shown in automation may never have reached the bank.",
            "A sale entered as cash may actually have been paid by card.",
            "Shift close relies on pump totals, not on bank confirmation.",
          ],
        },
        { type: "heading", text: "What bank-confirmed reconciliation adds" },
        {
          type: "paragraph",
          text: "Shift Tracker matches the sales data coming from automation against bank-confirmed payments, side by side. It corrects misclassified payment types, keeps bank-confirmed amounts separate from descriptive cash amounts, and records every correction with its reason, reviewer and timestamp.",
        },
        { type: "heading", text: "Automation and reconciliation together" },
        {
          type: "paragraph",
          text: "Pump automation measures the sale; Shift Tracker proves that sale reached the bank. Together they form a complete control that makes shift deficits and fraud visible. Contact us for a demo.",
        },
      ],
    },
  },
  {
    slug: "turpak-satis-banka-eslestirme",
    enSlug: "matching-turpak-sales-with-bank-payments",
    publishedAt: "2026-06-01",
    tr: {
      title: "TURPAK Satışını Banka Ödemeleriyle Eşleştirme Rehberi",
      seoTitle: "TURPAK Satışlarını Bankayla Eşleştirme - Shift Tracker",
      description:
        "Adım adım: TURPAK/otomasyon kart satışlarını banka onaylı ödemelerle nasıl eşleştirir, farkları nasıl çözer ve denetlenebilir kayıt oluşturursunuz.",
      body: [
        {
          type: "paragraph",
          text: "Otomasyon kart satışlarını kaydeder, ancak bu satışların bankaya geçtiğini doğrulamak ayrı bir adımdır. Bu rehber, TURPAK gibi sistemlerden gelen satış verisini banka onaylı ödemelerle eşleştirmenin pratik yolunu adım adım anlatır.",
        },
        { type: "heading", text: "Eşleştirme neden gerekir?" },
        {
          type: "paragraph",
          text: "Banka onayıyla karşılaştırılmayan bir satış toplamı, otomasyon içinde tutarlı görünse bile gerçeği yansıtmayabilir. Eşleştirme, her kart satışının banka tarafından onaylanmış bir kayda dayandığını güvence altına alır.",
        },
        { type: "heading", text: "Adım adım eşleştirme" },
        {
          type: "list",
          items: [
            "Vardiyayı açın ve ilgili otomasyon (TURPAK/Asis) satış verisini yükleyin.",
            "Kart ve nakit satışları ERP/otomasyon tarafında listeleyin.",
            "Banka onaylı ödemeleri yan yana karşılaştırın.",
            "Yanlış girilmiş ödeme tiplerini düzeltin (nakit ↔ kart).",
            "Tahsisleri oluşturun ve kalan tutarı çözüme bağlayın.",
            "Her adımı gerekçe, inceleyen ve zaman damgasıyla kaydedin.",
          ],
        },
        { type: "heading", text: "Sık karşılaşılan durumlar" },
        {
          type: "list",
          items: [
            "Nakit girilmiş ama kartla ödenmiş satış.",
            "Banka onaylı kısmi kart tutarı ve kalan nakit.",
            "Komşu vardiyaya ait olup yanlış vardiyada görünen ödeme.",
          ],
        },
        { type: "heading", text: "Sonuç" },
        {
          type: "paragraph",
          text: "Eşleştirme tamamlandığında her tutar ya banka onaylı bir kayda ya da açıklamalı bir nakit girişine bağlanır. Shift Tracker bu akışı otomatikleştirir; demo için bizimle iletişime geçin.",
        },
      ],
    },
    en: {
      title: "Matching TURPAK Sales with Bank Payments: A Step-by-Step Guide",
      seoTitle: "Matching TURPAK Sales with Bank Payments - Shift Tracker",
      description:
        "Step by step: how to match TURPAK/automation card sales with bank-confirmed payments, resolve differences, and produce an auditable record.",
      body: [
        {
          type: "paragraph",
          text: "Automation records card sales, but confirming those sales reached the bank is a separate step. This guide walks through the practical way to match sales data from systems like TURPAK against bank-confirmed payments.",
        },
        { type: "heading", text: "Why matching is needed" },
        {
          type: "paragraph",
          text: "A sales total that is never compared against bank confirmation may look consistent inside automation yet still not reflect reality. Matching guarantees that every card sale rests on a bank-confirmed record.",
        },
        { type: "heading", text: "The step-by-step match" },
        {
          type: "list",
          items: [
            "Open the shift and load the relevant automation (TURPAK/Asis) sales data.",
            "List the card and cash sales on the ERP/automation side.",
            "Compare bank-confirmed payments side by side.",
            "Correct any misclassified payment types (cash ↔ card).",
            "Create allocations and resolve the remaining amount.",
            "Record each step with its reason, reviewer and timestamp.",
          ],
        },
        { type: "heading", text: "Common cases" },
        {
          type: "list",
          items: [
            "A sale entered as cash that was actually paid by card.",
            "A bank-confirmed partial card amount with the remainder in cash.",
            "A payment that belongs to a neighboring shift but shows in the wrong one.",
          ],
        },
        { type: "heading", text: "The outcome" },
        {
          type: "paragraph",
          text: "Once matching is done, every amount traces to a bank-confirmed record or an explained cash entry. Shift Tracker automates this flow. Contact us for a demo.",
        },
      ],
    },
  },
  {
    slug: "kart-satisini-nakit-gosterme-suistimali",
    enSlug: "recording-card-sale-as-cash-fraud",
    publishedAt: "2026-05-31",
    tr: {
      title: "Kart Satışını Nakit Gösterme: Yaygın Bir İstasyon Suistimali",
      seoTitle: "Kart Satışını Nakit Gösterme Suistimali - Shift Tracker",
      description:
        "Kart satışının nakit (veya nakdin kart) gösterilmesi nasıl bir suistimaldir, neden fark edilmez ve banka onaylı mutabakatla nasıl engellenir?",
      modifiedAt: "2026-11-04",
      body: [
        {
          type: "paragraph",
          text: "Akaryakıt istasyonlarında en sessiz suistimallerden biri, bir kart satışının nakit (ya da nakdin kart) olarak kaydedilmesidir. Bu küçük bir veri girişi hatası gibi görünür; oysa banka onaylı toplam ile raporlanan tutar arasındaki bağı koparır ve aradaki farkın cebe atılmasına zemin hazırlar.",
        },
        {
          type: "callout",
          tone: "answer",
          title: "Kısa cevap",
          text: "Kartla ödenen bir satışın nakit girildiğini anlamanın güvenilir yolu, vardiyanın kart satışlarını bankanın işlem bazlı POS kayıtlarıyla karşılaştırmaktır: bankada olup otomasyonda karşılığı olmayan bir POS işlemi, nakit girilmiş bir kart satışına işaret eder. Ödeme tipi, orijinal kayıt, yeni tip ve gerekçe saklanarak karta düzeltilir; bankada karşılığı olmayan bir 'kartla ödendi' açıklaması ise nakit açığını kapatmaz.",
        },
        { type: "heading", text: "Ödeme tipi hatası ve suistimal nedir?" },
        {
          type: "paragraph",
          text: "Ödeme tipi hatası, otomasyonda veya ERP'de bir satışa kaydedilen ödeme tipinin (nakit, kredi kartı) müşterinin gerçekte kullandığı ödeme yöntemiyle uyuşmamasıdır. Yoğun bir vardiyada yanlış seçim yapmak gibi dürüst bir hatadan kaynaklanabilir; bilerek yapıldığında ise suistimale dönüşür.",
        },
        { type: "heading", text: "Suistimal nasıl işler?" },
        {
          type: "paragraph",
          text: "Kartla ödenen bir satış nakit olarak girildiğinde, sistem o tutarı nakit bekler. Vardiya sonunda nakit eksik çıktığında ise bu eksiklik, gerçek bir açık yerine bir giriş hatası gibi gösterilebilir. Tersi yönde, nakit satış kart gibi girilerek de benzer bir bulanıklık yaratılır.",
        },
        {
          type: "paragraph",
          text: "İki yönün bıraktığı iz farklıdır: nakit girilmiş bir kart satışı bankada eşleşmeyen bir POS işlemi bırakır; kart girilmiş bir nakit satış ise otomasyonda bankada karşılığı olmayan bir kart satışı bırakır.",
        },
        { type: "heading", text: "Neden fark edilmez?" },
        {
          type: "paragraph",
          text: "Satışlar banka kayıtlarıyla karşılaştırılmadığında, otomasyon toplamı kendi içinde tutarlı görünür. Banka onayı devreye girmediği için raporlanan ödeme tipi ile gerçekte bankaya geçen tutar arasındaki uyumsuzluk gizli kalır. Kontrol için gereken girdiler şunlardır:",
        },
        {
          type: "list",
          items: [
            "Otomasyonun işlem bazlı satış kayıtları: tutar, saat, pompa, personel ve ödeme tipi.",
            "Bankanın işlem bazlı POS kayıtları: tutar, terminal ve saat.",
            "POS gün sonu raporu: destekleyici bir belgedir, tek başına kanıt değildir.",
            "Vardiyanın nakit sayımı ve bankaya yatırma dekontları.",
          ],
        },
        { type: "heading", text: "Adım adım tespit ve ödeme tipi düzeltmesi" },
        {
          type: "orderedList",
          items: [
            "Vardiyanın kart satışlarını bankanın POS kayıtlarıyla eşleştirin: aynı tutar, aynı terminal ve yakın saat.",
            "Bankada olup otomasyonda karşılığı olmayan POS işlemlerini listeleyin.",
            "Her biri için aynı tutarda ve yakın saatte nakit girilmiş satışları arayın.",
            "Eşleşme güvenliyse ödeme tipini karta düzeltin; orijinal tipi, yeni tipi, gerekçeyi, düzelteni ve zamanı kaydedin.",
            "Düzeltilen satışı ilgili banka işlemine bağlayın; bir banka işlemi yalnızca bir satışı açıklayabilir.",
            "Ters yönü de kontrol edin: otomasyonda kart görünen ama bankada karşılığı olmayan satışlar.",
            "Kalan nakit farkını gerekçesiyle ilgili vardiyaya ve personele atayın.",
          ],
        },
        { type: "heading", text: "Örnek: nakit girilmiş 600 TL'lik kart satışı" },
        {
          type: "callout",
          tone: "example",
          title: "Örnek (temsili)",
          text: "Tutarlar yalnızca yöntemi göstermek içindir. Bir müşteri 600 TL'yi kartla ödüyor ama satış otomasyona nakit giriliyor. Vardiya sonunda kayıtlar 6.600 TL nakit bekliyor, sayılan nakit ise 6.000 TL; ilk bakışta 600 TL açık var. Bankada ise otomasyonda karşılığı olmayan 600 TL'lik bir POS işlemi duruyor. Satış karta düzeltildiğinde kart tarafı bankayla tutar ve nakit farkı kapanır. Bankada böyle bir işlem yoksa 'aslında kartla ödendi' açıklaması doğrulanamaz ve 600 TL gerçek bir açık olarak incelenir.",
        },
        {
          type: "table",
          table: {
            caption: "Temsili vardiya: nakit girilmiş 600 TL'lik kart satışı (tutarlar örnektir)",
            headers: ["Kalem", "Düzeltme öncesi", "Düzeltme sonrası"],
            rows: [
              ["Kart satışı (otomasyon)", "13.400 TL", "14.000 TL"],
              ["Kart işlemleri (banka POS)", "14.000 TL", "14.000 TL"],
              ["Beklenen nakit", "6.600 TL", "6.000 TL"],
              ["Sayılan nakit", "6.000 TL", "6.000 TL"],
              ["Nakit farkı", "600 TL eksik", "0 TL"],
            ],
          },
        },
        { type: "heading", text: "İstisnalar ve elle inceleme gereken durumlar" },
        {
          type: "list",
          items: [
            "Aynı tutarda birden fazla işlem: hangi satışın hangi POS işlemine ait olduğu saat, terminal ve pompa bilgisiyle elle belirlenmelidir.",
            "Kısmi ödeme: bir kısmı kartla, kalanı nakit ödenen satış tek bir ödeme tipine çevrilmemeli; kart ve nakit kısımları ayrı izlenmelidir.",
            "Banka kaydı henüz gelmemişse: valör ve gün sonu dosyasının zamanlaması nedeniyle düzeltmeden önce banka kaydını bekleyin.",
            "Komşu vardiya: devir saatine yakın bir POS işlemi, başka bir vardiyadaki satışa ait olabilir.",
            "Banka POS'u dışındaki ödemeler: filo veya ön ödemeli kart gibi ödemeler bankanın POS kayıtlarında görünmeyebilir.",
          ],
        },
        {
          type: "callout",
          tone: "limitation",
          title: "Dikkat edilmesi gerekenler",
          text: "Tek bir ödeme tipi hatası suistimal kanıtı değildir; çoğu zaman dürüst bir giriş hatasıdır. Aynı personelde tekrarlayan düzeltmeler veya hep aynı yönde çıkan farklar gibi örüntüler ayrıca incelenmelidir. Düzeltmeyi kimin yapabileceği ve kimin onayına bağlı olduğu da işletmenin iç kontrol kurallarında yer almalıdır.",
        },
        { type: "heading", text: "Shift Tracker'da ödeme tipi düzeltmesi" },
        // Sources: languageData.tsx:3974 (tr.home.features: bütün ödemeler bankayla karşılaştırılır,
        // eşleşmeyen her tutar öne çıkarılır), languageData.tsx:4387 (tr.fraud.gaps: ödeme tipi
        // uyumsuzluğu ortaya çıkar), landingPages.ts:146 (orijinal ödeme tipi, yeni tip, gerekçe,
        // kullanıcı, zaman), languageData.tsx:5066 (tr ödeme tipi düzeltme sayfası: banka eşleştirmesi
        // oluşturmaz) ve 4053 (tr.home.examples: düzeltilen satış mutabakatta görünür, banka hareketine
        // bağlanabilir), languageData.tsx:4379 (tr.fraud.gaps: her ödeme yalnızca bir kez tahsis
        // edilir, tek kanıt iki tutarı haklı çıkaramaz), landingPages.ts:972 (bankaya ulaşmayan kart
        // satışı inceleme için öne çıkar)
        {
          type: "callout",
          tone: "product",
          title: "Shift Tracker bunu nasıl yapar?",
          segments: [
            { text: "Shift Tracker otomasyon satışlarını banka onaylı ödemelerle eşleştirir ve eşleşmeyen her tutarı öne çıkarır; böylece raporlanan ödeme tipi ile gerçekte bankaya geçen tutar arasındaki uyumsuzluk görünür olur. Nakit girilmiş ama kartla ödenmiş bir satış karta düzeltildiğinde orijinal ödeme tipi, yeni ödeme tipi, gerekçe, kullanıcı ve zaman saklanır. Düzeltme tek başına banka eşleştirmesi oluşturmaz: satış mutabakatta görünür hale gelir ve banka hareketine orada bağlanır. Her ödeme yalnızca bir kez tahsis edilebildiği için aynı POS slibi iki satışı açıklayamaz; bankaya hiç ulaşmamış bir kart satışı da inceleme için öne çıkarılır. Ayrıntılar: " },
            { text: "kredi kartı ödeme doğrulama", to: "/kredi-karti-odeme-dogrulama/" },
            { text: " ve " },
            { text: "akaryakıt istasyonunda suistimal önleme", to: "/fraud-prevention/" },
            { text: "." },
          ],
        },
        { type: "heading", text: "Görünürlük caydırıcıdır" },
        {
          type: "paragraph",
          segments: [
            { text: "Her ödeme tipi bankaya göre doğrulandığında ve her düzeltme kayıt altına alındığında, bu suistimalin sessizce geçmesi çok zorlaşır. Düzeltmelerden sonra kalan nakit farkının nasıl ele alınacağını " },
            { text: "vardiya mutabakatı ve personel açığı", to: "/blog/vardiya-mutabakati-ve-personel-acigi/" },
            { text: " rehberinde anlattık. Shift Tracker, günlük mutabakatınızı bir suistimal önleme kontrolüne dönüştürür; demo için bizimle iletişime geçin." },
          ],
        },
      ],
    },
    en: {
      title: "Recording a Card Sale as Cash: A Common Station Fraud",
      seoTitle: "Recording Card Sales as Cash: A Common Fraud - Shift Tracker",
      description:
        "How recording a card sale as cash (or cash as card) becomes fraud, why it goes unnoticed, and how bank-confirmed reconciliation prevents it.",
      modifiedAt: "2026-11-04",
      body: [
        {
          type: "paragraph",
          text: "One of the quietest frauds at a fuel station is recording a card sale as cash (or cash as card). It looks like a small data-entry slip, but it breaks the link between the bank-confirmed total and the reported figure, creating room to pocket the difference.",
        },
        {
          type: "callout",
          tone: "answer",
          title: "Short answer",
          text: "The reliable way to tell whether a card-paid sale was entered as cash is to compare the shift's card sales with the bank's transaction-level POS records: a POS transaction at the bank with no matching card sale in the automation points to a card sale recorded as cash. The payment type is then corrected to card, keeping the original entry, the new type and the reason; a claim that a sale was paid by card, with no transaction at the bank behind it, does not close a cash shortfall.",
        },
        { type: "heading", text: "What a payment type error, and the fraud, are" },
        {
          type: "paragraph",
          text: "A payment type error is when the payment type recorded for a sale in the automation or ERP (cash, credit card) does not match how the customer actually paid. It can be an honest slip on a busy shift, such as picking the wrong option; done on purpose, it becomes fraud.",
        },
        { type: "heading", text: "How the trick works" },
        {
          type: "paragraph",
          text: "When a card-paid sale is entered as cash, the system expects that amount in cash. If cash comes up short at shift close, the shortfall can be passed off as a data-entry error rather than a real deficit. Entering a cash sale as card creates the same kind of blur in the other direction.",
        },
        {
          type: "paragraph",
          text: "The two directions leave different traces: a card sale recorded as cash leaves an unmatched POS transaction at the bank, while a cash sale recorded as card leaves a card sale in the automation with nothing at the bank behind it.",
        },
        { type: "heading", text: "Why it goes unnoticed" },
        {
          type: "paragraph",
          text: "When sales are not compared against bank records, the automation total looks internally consistent. Because bank confirmation never enters the picture, the mismatch between the reported payment type and what actually reached the bank stays hidden. These are the inputs you need for the check:",
        },
        {
          type: "list",
          items: [
            "The automation's transaction-level sales: amount, time, pump, employee and payment type.",
            "The bank's transaction-level POS records: amount, terminal and time.",
            "The POS end-of-day report: supporting evidence, not proof on its own.",
            "The shift's cash count and the bank deposit receipts.",
          ],
        },
        { type: "heading", text: "Detecting it and correcting the payment type, step by step" },
        {
          type: "orderedList",
          items: [
            "Match the shift's card sales to the bank's POS records: the same amount, the same terminal and a close time.",
            "List the POS transactions at the bank that have no matching sale in the automation.",
            "For each one, look for a sale entered as cash with the same amount at a similar time.",
            "When the match is confident, correct the payment type to card and record the original type, the new type, the reason, who corrected it and when.",
            "Link the corrected sale to its bank transaction; one bank transaction can explain only one sale.",
            "Check the other direction too: sales shown as card in the automation with no transaction at the bank.",
            "Attribute any remaining cash difference, with a reason, to the relevant shift and employee.",
          ],
        },
        { type: "heading", text: "Example: a TRY 600 card sale entered as cash" },
        {
          type: "callout",
          tone: "example",
          title: "Example (illustrative)",
          text: "The amounts only show the method. A customer pays TRY 600 by card, but the sale is entered in the automation as cash. At shift close the records expect TRY 6,600 in cash while TRY 6,000 is counted, so cash looks TRY 600 short. At the bank, though, there is a TRY 600 POS transaction with no matching card sale in the automation. Once the sale is corrected to card, the card side agrees with the bank and the cash gap closes. If no such transaction exists at the bank, the explanation that the sale was really paid by card cannot be verified, and the TRY 600 is reviewed as a genuine deficit.",
        },
        {
          type: "table",
          table: {
            caption: "Illustrative shift: a TRY 600 card sale entered as cash (example amounts)",
            headers: ["Item", "Before correction", "After correction"],
            rows: [
              ["Card sales (automation)", "TRY 13,400", "TRY 14,000"],
              ["Card transactions (bank POS)", "TRY 14,000", "TRY 14,000"],
              ["Expected cash", "TRY 6,600", "TRY 6,000"],
              ["Cash counted", "TRY 6,000", "TRY 6,000"],
              ["Cash difference", "TRY 600 short", "TRY 0"],
            ],
          },
        },
        { type: "heading", text: "Exceptions and when to review by hand" },
        {
          type: "list",
          items: [
            "Several transactions with the same amount: decide by hand which sale each POS transaction belongs to, using the time, terminal and pump.",
            "Partial payments: do not switch a sale paid partly by card and partly in cash to a single payment type; track the card and cash portions separately.",
            "Bank record not in yet: because of value dates and end-of-day file timing, wait for the bank record before correcting.",
            "Neighboring shift: a POS transaction close to handover may belong to a sale in another shift.",
            "Payments outside the bank POS: fleet or prepaid card payments may not appear in the bank's POS records.",
          ],
        },
        {
          type: "callout",
          tone: "limitation",
          title: "Keep in mind",
          text: "A single payment type error is not proof of fraud; most of the time it is an honest entry mistake. Patterns such as repeated corrections for the same employee, or differences that always go the same way, deserve a separate look. Who may make a correction, and whose approval it needs, should also be part of the business's internal controls.",
        },
        { type: "heading", text: "Payment type correction in Shift Tracker" },
        // Sources: languageData.tsx:1411 (eng.home.features: every payment checked against the bank,
        // anything that does not match is surfaced), languageData.tsx:1824 (eng.fraud.gaps: payment
        // type mismatch is exposed), landingPages.ts:296 (original type, new type, reason, user,
        // time), languageData.tsx:2503 (eng payment type correction page: does not create bank
        // matching) and 1490 (eng.home.examples: corrected sale becomes visible in reconciliation and
        // can be linked to bank activity), languageData.tsx:1816 (eng.fraud.gaps: every payment can be
        // allocated only once, one proof never justifies two amounts), landingPages.ts:1049 (a card
        // sale that never reached the bank is surfaced for review)
        {
          type: "callout",
          tone: "product",
          title: "What Shift Tracker does",
          segments: [
            { text: "Shift Tracker matches automation sales against bank-confirmed payments and surfaces every amount that does not match, so a mismatch between the reported payment type and what actually reached the bank becomes visible. When a sale entered as cash but paid by card is corrected to card, the original payment type, the new type, the reason, the user and the time are kept. The correction does not create the bank match on its own: the sale becomes visible in reconciliation and is linked to the bank transaction there. Because every payment can be allocated only once, the same POS receipt cannot explain two sales, and a card sale that never reached the bank is surfaced for review. More on " },
            { text: "credit card payment verification", to: "/en/credit-card-payment-verification/" },
            { text: " and " },
            { text: "fraud prevention at fuel stations", to: "/en/fraud-prevention/" },
            { text: "." },
          ],
        },
        { type: "heading", text: "Visibility is the deterrent" },
        {
          type: "paragraph",
          segments: [
            { text: "When every payment type is verified against the bank and every correction is on record, this fraud becomes very hard to pass silently. We cover how to handle the cash difference that remains after corrections in our guide to " },
            { text: "shift reconciliation and employee deficits", to: "/en/blog/shift-reconciliation-employee-deficits/" },
            { text: ". Shift Tracker turns your daily reconciliation into a fraud-prevention control. Contact us for a demo." },
          ],
        },
      ],
    },
  },
  {
    slug: "vardiyayi-bir-dakikada-nasil-kapatirsiniz",
    enSlug: "how-to-close-a-shift-in-one-minute",
    publishedAt: "2026-07-12",
    tr: {
      title: "Bir Vardiyayı Bir Dakikanın Altında Nasıl Kapatırsınız",
      seoTitle: "Vardiyayı Bir Dakikada Nasıl Kapatırsınız? - Shift Tracker",
      description:
        "Vardiya kapanışı adım adım: mutabakat sizin için hazırdır, siz yalnızca rakamları gözden geçirip vardiyayı kilitlersiniz. İki saatlik iş bir dakikaya iner.",
      body: [
        {
          type: "paragraph",
          text: "Klasik vardiya kapanışı bir akşamı yiyip bitirir: satışları elle toplamak, kart sliplerini banka tutarlarıyla tek tek karşılaştırmak, nakdin bankaya ulaşıp ulaşmadığını takip etmek. Shift Tracker'da bu iş vardiya kapanmadan çoktan yapılmıştır; size yalnızca sonucu onaylamak kalır. Bu rehber, bir vardiyayı bir dakikanın altında nasıl kapatacağınızı adım adım anlatır.",
        },
        { type: "heading", text: "Siz başlamadan önce sistem ne yaptı?" },
        {
          type: "paragraph",
          text: "Pompa otomasyonunuzdan (TURPAK, Asis) gelen kart satışları, banka onaylı ödemelerle düzenli olarak eşleştirilir. Bankaların gün sonu dosyaları geldikçe kart tarafı arka planda mutabık kılınır; böylece siz vardiyayı gözden geçirmeye oturduğunuzda kart satışları çoktan doğrulanmış olur.",
        },
        { type: "heading", text: "Adım 1: Vardiya incelemesini açın" },
        {
          type: "paragraph",
          text: "Panelde ilgili vardiyayı seçin. Shift Tracker, o vardiyanın ERP toplamlarını, banka onaylı kart tutarlarını ve nakit hareketlerini tek ekranda önünüze getirir. Hiçbir rakamı elle girmezsiniz.",
        },
        { type: "heading", text: "Adım 2: Rakamları gözden geçirin" },
        {
          type: "paragraph",
          text: "Eşleşen satırlar zaten yeşildir; banka ile tutan tutarlar için yapılacak bir şey yoktur. Gözünüz yalnızca dikkat isteyen satırlarda kalır: eksik, fazla veya belirsiz olarak işaretlenmiş kayıtlar.",
        },
        { type: "heading", text: "Adım 3: Nakit yatırmayı doğrulayın" },
        {
          type: "paragraph",
          text: "Nakit yatırma, gerçek banka ekstrenizle karşılaştırılarak doğrulanır. Yatan tutar beklenenle eşleşiyorsa vardiya nakit tarafında da temizdir; eksik veya mükerrer bir yatırma varsa sistem bunu işaretler.",
        },
        { type: "heading", text: "Adım 4: İşaretli farkları çözün" },
        {
          type: "paragraph",
          text: "Çözülmemiş bir fark varsa, açık bir gerekçeyle ilgili vardiyaya ve personele atanır. Kararı siz verirsiniz; sistem her düzeltmeyi, gerekçeyi ve zaman damgasını kayıt altına alır. Böylece açık, kaybolmak yerine denetlenebilir bir kayda dönüşür.",
        },
        { type: "heading", text: "Adım 5: Vardiyayı kilitleyin" },
        {
          type: "paragraph",
          text: "Rakamlar doğrulandığında vardiyayı kilitlersiniz. Kilitli vardiya değiştirilemez ve o anki hesap, kimin neyi onayladığıyla birlikte kalıcı bir kayıt olarak saklanır. Denetime hazırsınız.",
        },
        { type: "heading", text: "Neden iki saat değil, bir dakika?" },
        {
          type: "paragraph",
          text: "Çünkü zor iş, yani eşleştirme ve doğrulama, siz oturmadan önce otomatik yapılmıştır. Size yalnızca kararların verildiği son adım kalır. Manuel karşılaştırmayı ortadan kaldırdığınızda, vardiya kapanışı bir angarya olmaktan çıkıp hızlı bir onaya döner.",
        },
        {
          type: "paragraph",
          text: "Shift Tracker'ı kendi istasyonunuzun rakamlarıyla görmek için bizimle iletişime geçin.",
        },
      ],
    },
    en: {
      title: "How to Close a Shift in Under a Minute",
      description:
        "Closing a shift step by step: the reconciliation is already done for you, so you just review the figures and lock it. Two hours of work becomes one minute.",
      body: [
        {
          type: "paragraph",
          text: "The classic shift close eats an evening: adding up sales by hand, comparing card slips to bank amounts one by one, chasing whether the cash reached the bank. In Shift Tracker that work is already done before the shift closes, so all that is left for you is to confirm the result. This guide walks through closing a shift in under a minute, step by step.",
        },
        { type: "heading", text: "What the system did before you started" },
        {
          type: "paragraph",
          text: "Card sales from your pump automation (TURPAK, Asis) are matched to bank-confirmed payments on a schedule. As the banks' end-of-day files arrive, the card side is reconciled in the background, so by the time you sit down to review the shift, the card sales are already verified.",
        },
        { type: "heading", text: "Step 1: Open the shift review" },
        {
          type: "paragraph",
          text: "Select the shift in the dashboard. Shift Tracker brings that shift's ERP totals, bank-confirmed card amounts and cash movements onto a single screen. You type no figures by hand.",
        },
        { type: "heading", text: "Step 2: Review the figures" },
        {
          type: "paragraph",
          text: "Matched rows are already green; amounts that agree with the bank need nothing from you. Your attention only lands on the rows that ask for it: records flagged as short, over, or ambiguous.",
        },
        { type: "heading", text: "Step 3: Confirm the cash deposit" },
        {
          type: "paragraph",
          text: "The cash deposit is verified against your real bank statement. If the deposited amount matches what was expected, the shift is clean on the cash side too; if a deposit is short or duplicated, the system flags it.",
        },
        { type: "heading", text: "Step 4: Resolve any flagged differences" },
        {
          type: "paragraph",
          text: "If a difference is unresolved, it is attributed to the relevant shift and employee with a clear reason. You make the call; the system records every correction, its reason and a timestamp. The deficit becomes an auditable record instead of disappearing.",
        },
        { type: "heading", text: "Step 5: Lock the shift" },
        {
          type: "paragraph",
          text: "Once the figures are confirmed, you lock the shift. A locked shift cannot be altered, and its final account is kept as a permanent record of who approved what. You are audit-ready.",
        },
        { type: "heading", text: "Why a minute, not two hours?" },
        {
          type: "paragraph",
          text: "Because the hard part, the matching and verification, is done automatically before you sit down. All that is left for you is the final step where the decisions are made. Once you remove the manual comparison, closing a shift stops being a chore and becomes a quick approval.",
        },
        {
          type: "paragraph",
          text: "Contact us to see Shift Tracker run against your own station's numbers.",
        },
      ],
    },
  },
  {
    slug: "shift-tracker-mutabakati-nasil-calisir",
    enSlug: "how-shift-tracker-reconciliation-works",
    publishedAt: "2026-07-11",
    tr: {
      title: "Shift Tracker Mutabakatı Nasıl Çalışır?",
      description:
        "İki bağımsız gerçek (ERP satışları ve banka ödemeleri) nasıl eşleştirilir, sistem neden tam resmi bekler ve her tutar nasıl ispatlanabilir hale gelir.",
      body: [
        {
          type: "paragraph",
          text: "Shift Tracker'ın kalbinde tek bir soru vardır: bu satış gerçekten bankaya geçti mi? Bu soruyu yanıtlamak için sistem iki bağımsız veri kaynağını karşılaştırır. Bu yazı, mutabakatın perde arkasında nasıl çalıştığını anlatır.",
        },
        { type: "heading", text: "İki bağımsız gerçek" },
        {
          type: "paragraph",
          text: "Birincisi, pompa otomasyonundan gelen ERP satışıdır: bu, satış niyetidir. Kartla ödendiği söylenen bir satır, tek başına ödemenin gerçekleştiğinin kanıtı değildir. İkincisi, bankanın POS hareketleridir: bu, gerçek para hareketinin kanıtıdır. Mutabakat, bu ikisini birbirine bağlar.",
        },
        { type: "heading", text: "Otomatik eşleştirme nasıl olur?" },
        {
          type: "paragraph",
          text: "Sistem, kart satışlarını doğru banka hareketiyle eşleştirir: aynı tutar, aynı terminal ve dar bir zaman aralığı. Bir satış birden fazla banka hareketine değil, yalnızca bir hareketle eşleşebilir; böylece aynı ödeme iki kez sayılamaz. Güvenli eşleşmeler onaylanır, belirsiz olanlar incelemeye bırakılır.",
        },
        { type: "heading", text: "Sistem neden tam resmi bekler?" },
        {
          type: "paragraph",
          text: "Bankaların gün sonu dosyaları farklı saatlerde gelir. Sistem, bir günün mutabakatını, o güne ait tüm aktif bankaların dosyaları gelene kadar bekletir. Böylece bir satış, doğru dosyası henüz gelmemişken yanlış bir harekete kilitlenmez. Eksik resimle asla eşleştirme yapılmaz.",
        },
        { type: "heading", text: "Nakit yatırmanın doğrulanması" },
        {
          type: "paragraph",
          text: "Kart tarafı bankayla mutabık kılınırken nakit tarafı da ihmal edilmez. Her nakit yatırma için benzersiz bir referans üretilir; bu referans dekonta yazılır. Sistem, banka ekstresinde bu referansı ve tutarı arar ve yatırmayı otomatik olarak doğrular.",
        },
        { type: "heading", text: "Ekranda ne görürsünüz?" },
        {
          type: "paragraph",
          text: "Her satır üç durumdan birindedir: eşleşti (banka ile tutuyor), inceleme gerekiyor (belirsiz veya tutarsız) veya açık (fark ilgili vardiyaya atandı). Yeşil satırlar için yapılacak bir şey yoktur; dikkatiniz yalnızca gerçekten gerektiren yere gider.",
        },
        { type: "heading", text: "Her tutar ispatlanabilir" },
        {
          type: "paragraph",
          text: "Mutabakat bittiğinde, istasyondaki her tutar gerçek bir banka işlemine dayanır. Her düzeltmenin gerekçesi, sorumlusu ve zamanı kayıtlıdır. Bu, güvene değil kanıta dayalı bir istasyon yönetimidir.",
        },
        {
          type: "paragraph",
          text: "Bu akışın kendi istasyonunuzda nasıl çalıştığını görmek için bizimle iletişime geçin.",
        },
      ],
    },
    en: {
      title: "How Shift Tracker Reconciliation Works",
      description:
        "How two independent truths (ERP sales and bank payments) get matched, why the system waits for the full picture, and how every amount becomes provable.",
      body: [
        {
          type: "paragraph",
          text: "At the heart of Shift Tracker is a single question: did this sale actually reach the bank? To answer it, the system compares two independent sources of data. This article explains how reconciliation works behind the scenes.",
        },
        { type: "heading", text: "Two independent truths" },
        {
          type: "paragraph",
          text: "The first is the ERP sale from your pump automation: this is the sale intent. A row that says it was paid by card is not, on its own, proof that the payment settled. The second is the bank's POS transactions: this is the evidence of real money movement. Reconciliation links the two.",
        },
        { type: "heading", text: "How automatic matching works" },
        {
          type: "paragraph",
          text: "The system matches a card sale to the right bank transaction: the same amount, the same terminal, and a narrow time window. A sale can be matched to only one bank transaction, never several, so the same payment can never be counted twice. Confident matches are confirmed; ambiguous ones are left for review.",
        },
        { type: "heading", text: "Why the system waits for the full picture" },
        {
          type: "paragraph",
          text: "The banks' end-of-day files arrive at different times. The system holds a day's reconciliation until every active bank's file for that day has landed. That way a sale is never locked onto the wrong transaction while its correct file has not arrived yet. It never matches against an incomplete picture.",
        },
        { type: "heading", text: "Verifying the cash deposit" },
        {
          type: "paragraph",
          text: "While the card side is reconciled with the bank, the cash side is not left out. Each cash deposit gets a unique reference that is written on the deposit slip. The system looks for that reference and amount on your bank statement and verifies the deposit automatically.",
        },
        { type: "heading", text: "What you see on screen" },
        {
          type: "paragraph",
          text: "Every row is in one of three states: matched (it agrees with the bank), needs review (ambiguous or inconsistent), or deficit (the difference was attributed to the relevant shift). Green rows need nothing from you; your attention only goes where it is genuinely needed.",
        },
        { type: "heading", text: "Every amount is provable" },
        {
          type: "paragraph",
          text: "When reconciliation is done, every amount at the station traces back to a real bank transaction. Every correction has a recorded reason, owner and time. This is station management based on proof, not trust.",
        },
        {
          type: "paragraph",
          text: "Contact us to see how this flow works on your own station.",
        },
      ],
    },
  },
  {
    slug: "kredi-karti-gun-sonu-raporu-banka-mutabakati",
    enSlug: "pos-end-of-day-report-vs-bank-settlement",
    publishedAt: "2026-07-14",
    tr: {
      title: "Kredi Kartı Gün Sonu Raporu Banka ile Eşleşiyor mu? Mutabakat Yapıldı mı?",
      seoTitle: "Kredi Kartı Gün Sonu Raporu ve Banka Kaydı - Shift Tracker",
      description:
        "POS gün sonu (Z) raporundaki kart tutarı bankaya geçenle tutmalı. Ama kağıt rapor mükerrer veya düzeltilmiş olabilir; tek güvenilir kanıt banka kaydıdır.",
      body: [
        {
          type: "paragraph",
          text: "Çoğu istasyonda gün sonu kontrolü tek bir kağıda dayanır: POS cihazından alınan gün sonu (Z) raporu. Mantık basit görünür; cihazın yazdığı kart toplamı, bankaya geçen tutarla birebir tutmalıdır. Peki o kağıdın kendisi ne kadar güvenilir? Bu yazıda, gün sonu raporuna güvenmenin neden yeterli olmadığını ve gerçek mutabakatın nasıl yapıldığını anlatıyoruz.",
        },
        { type: "heading", text: "Gün sonu (Z) raporu neyi kanıtlar, neyi kanıtlamaz?" },
        {
          type: "paragraph",
          text: "Gün sonu raporu, POS cihazının o gün çektiği kart işlemlerinin cihaz üzerindeki toplamıdır. Bu, cihazın kendi beyanıdır; paranın gerçekten hesabınıza geçtiğinin kanıtı değildir. Kanıt yalnızca bankanın kendi kaydında, yani hesabınıza düşen valörlü tutarda vardır.",
        },
        { type: "heading", text: "Kağıt gün sonu raporu neden yanıltıcı olabilir?" },
        {
          type: "list",
          items: [
            "Mükerrer: Aynı gün sonu raporu birden fazla kez kullanılıp farklı açıkların üstünü kapatabilir.",
            "Başka güne ait: Bir günün raporu, başka bir günün kasasını denk göstermek için kullanılabilir.",
            "Başka bir firmadan: Benzer bir işletmenin ya da başka bir cihazın raporu, sizinki gibi sunulabilir.",
            "Düzeltilmiş kopya: Nakit açığını gizlemek için tutarı düşürülmüş, elden geçirilmiş bir kopya verilebilir.",
          ],
        },
        {
          type: "paragraph",
          segments: [
            { text: "Ortak nokta şudur: kağıt bir rapor, onu üreten kişinin beyanıdır ve elle değiştirilebilir. Bu tür " },
            { text: "dolandırıcılık açıkları", to: "/fraud-prevention/" },
            { text: ", sıkı bir denetim ve banka doğrulaması olmadan görünmez kalır." },
          ],
        },
        { type: "heading", text: "Gerçek mutabakat: kart satışını bankanın kaydıyla eşleştirmek" },
        {
          type: "paragraph",
          segments: [
            { text: "Doğru soru 'gün sonu raporu kasayla tutuyor mu' değil, 'her kart satışı bankanın kaydıyla eşleşiyor mu' olmalı. Shift Tracker, otomasyondan gelen her kart satışını doğrudan bankanın gün sonu mutabakat dosyasıyla eşleştirir; " },
            { text: "mutabakatın nasıl çalıştığını buradan görebilirsiniz", to: "/blog/shift-tracker-mutabakati-nasil-calisir/" },
            { text: ". Kanıt cihazın kağıdı değil, bankanın kendi kaydıdır." },
          ],
        },
        {
          type: "paragraph",
          segments: [
            { text: "Bu, aynı zamanda bir " },
            { text: "kredi kartı ödeme doğrulama", to: "/kredi-karti-odeme-dogrulama/" },
            { text: " katmanıdır: kartla ödendiği söylenen her tutar, gerçekten bankaya geçip geçmediğine göre teyit edilir." },
          ],
        },
        { type: "heading", text: "Artık kağıt gün sonu raporuyla uğraşmanıza gerek yok" },
        {
          type: "paragraph",
          segments: [
            { text: "Kart tarafı banka kaydıyla otomatik doğrulandığında, gün sonu raporunu elle toplama, karşılaştırma ve saklama zahmeti ortadan kalkar. Vardiya siz açtığınızda çoktan mutabık kılınmıştır; size yalnızca gözden geçirip kilitlemek kalır. Nasıl olduğunu " },
            { text: "bir vardiyayı bir dakikanın altında kapatma", to: "/blog/vardiyayi-bir-dakikada-nasil-kapatirsiniz/" },
            { text: " yazısında adım adım anlattık." },
          ],
        },
        {
          type: "paragraph",
          text: "Özetle: kağıt gün sonu raporu bir başlangıç noktasıdır, kanıt değildir. Kanıt, her kuruşun bankanın kendi kaydına dayanmasıdır. İstasyonunuzda bunun nasıl çalıştığını görmek için bizimle iletişime geçin.",
        },
      ],
    },
    en: {
      title: "Does Your POS End-of-Day Report Match the Bank? Has It Been Reconciled?",
      seoTitle: "POS End-of-Day Report vs Bank Settlement - Shift Tracker",
      description:
        "The card total on your POS end-of-day (Z) slip should match the bank. But a paper slip can be a duplicate or doctored; only the bank's own record is proof.",
      body: [
        {
          type: "paragraph",
          text: "At most stations the end-of-day check rests on a single piece of paper: the end-of-day (Z) report printed by the POS device. The logic looks simple; the card total the device prints should match the amount that reached the bank exactly. But how trustworthy is that piece of paper itself? This article explains why trusting the end-of-day report is not enough, and how real reconciliation is done.",
        },
        { type: "heading", text: "What the end-of-day (Z) report does and does not prove" },
        {
          type: "paragraph",
          text: "The end-of-day report is the device's own total of the card transactions it ran that day. It is the device's own statement; it is not proof that the money actually reached your account. That proof exists only in the bank's own record, in the settled amount that lands in your account.",
        },
        { type: "heading", text: "Why a paper end-of-day report can mislead" },
        {
          type: "list",
          items: [
            "Duplicate: the same end-of-day report is used more than once to cover different shortfalls.",
            "From another day: one day's report is used to make another day's register look balanced.",
            "From another company: a similar business's or another device's report is presented as yours.",
            "Doctored copy: a reworked copy with a reduced total is handed over to hide skimmed cash.",
          ],
        },
        {
          type: "paragraph",
          segments: [
            { text: "The common thread: a paper report is the statement of whoever produced it, and it can be altered by hand. These kinds of " },
            { text: "fraud gaps", to: "/en/fraud-prevention/" },
            { text: " stay invisible without a tight audit and bank verification." },
          ],
        },
        { type: "heading", text: "Real reconciliation: matching the card sale to the bank's record" },
        {
          type: "paragraph",
          segments: [
            { text: "The right question is not 'does the end-of-day report match the register' but 'does every card sale match the bank's record'. Shift Tracker matches every card sale from your automation directly to the bank's end-of-day settlement file; you can " },
            { text: "see how reconciliation works here", to: "/en/blog/how-shift-tracker-reconciliation-works/" },
            { text: ". The proof is the bank's own record, not the device's paper." },
          ],
        },
        {
          type: "paragraph",
          segments: [
            { text: "This is also a layer of " },
            { text: "credit card payment verification", to: "/en/credit-card-payment-verification/" },
            { text: ": every amount said to be paid by card is confirmed by whether it actually reached the bank." },
          ],
        },
        { type: "heading", text: "You no longer have to deal with the paper end-of-day report" },
        {
          type: "paragraph",
          segments: [
            { text: "Once the card side is verified against the bank automatically, the chore of adding up, comparing and filing the end-of-day report disappears. The shift is already reconciled by the time you open it; all that is left is to review and lock it. We walk through exactly how in " },
            { text: "how to close a shift in under a minute", to: "/en/blog/how-to-close-a-shift-in-one-minute/" },
            { text: "." },
          ],
        },
        {
          type: "paragraph",
          text: "In short: the paper end-of-day report is a starting point, not proof. Proof is every amount tracing back to the bank's own record. Contact us to see how this works on your own station.",
        },
      ],
    },
  },
  {
    slug: "kredi-karti-satisi-bankaya-ne-zaman-gecer",
    enSlug: "when-do-card-sales-reach-the-bank",
    publishedAt: "2026-07-13",
    tr: {
      title: "Kredi Kartı Satışı Bankaya Ne Zaman Geçer? Valör ve Blokaj Rehberi",
      seoTitle: "Kredi Kartı Satışı Bankaya Ne Zaman Geçer? - Shift Tracker",
      description:
        "Kart satışının parası neden aynı gün hesapta görünmüyor? Valör, blokaj, komisyon ve taksitin POS toplamını banka tutarından nasıl ayırdığını anlatıyoruz.",
      modifiedAt: "2026-11-04",
      body: [
        {
          type: "paragraph",
          text: "Kart satışını yaptınız ama para neden aynı gün hesapta görünmüyor? Bu bir hata değil; kredi kartı ödemelerinin bankaya geçiş biçimidir. Bu yazıda valör, blokaj, komisyon ve taksitin POS toplamını neden banka tutarından ayırdığını anlatıyoruz.",
        },
        {
          type: "callout",
          tone: "answer",
          title: "Kısa cevap",
          text: "Kart satışının parası satış anında değil, bankanın valör gününde hesaba geçer; valör günü ve varsa blokaj süresi bankaya ve üye işyeri anlaşmasına göre değişir. Hesaba geçen tutar da komisyon, iade ve benzeri kesintilerden sonraki net tutardır. Bu yüzden POS toplamının aynı gün banka tutarına eşit olmaması, fark banka ekstresiyle açıklanabildiği sürece bir açık değildir.",
        },
        { type: "heading", text: "Valör, blokaj ve komisyon nedir?" },
        {
          type: "paragraph",
          text: "Kart işlemi POS'ta anında onaylanır, ancak bankanın işlem kaydı ve paranın hesaba aktarılması ayrı adımlardır. Bir vardiyanın kart satışları banka tarafında çoğu zaman ancak ertesi gün görünür; paranın hesaba geçtiği gün ise anlaşmanıza bağlıdır.",
        },
        {
          type: "list",
          items: [
            "Valör: işlemin hesaba gerçekten geçtiği, tutarın kullanılabilir olduğu değer günüdür.",
            "Blokaj: tutarın hesaba aktarılmadan önce bekletildiği süredir. Bazı anlaşmalarda blokaj yoktur, bazılarında belirli bir süre uygulanır; süre bankaya ve anlaşmaya göre değişir.",
            "Komisyon: bankanın işlemlerden kestiği hizmet bedelidir; hesaba geçen, brüt satıştan komisyon düşülmüş net tutardır. Oran bankaya ve anlaşmaya göre değişir.",
            "Taksit: taksitli bir işlem varsa, tutarın tek seferde mi yoksa parça parça mı aktarılacağı anlaşmaya bağlıdır.",
            "İade ve iptal: sonradan yapılan iade veya iptaller hesaba geçen tutarı değiştirir ve ekstrede genellikle ayrı satır olarak görünür.",
          ],
        },
        {
          type: "paragraph",
          segments: [
            { text: "Mutabakat için gereken girdiler: otomasyonun vardiya raporundaki kart satışları, POS gün sonu (Z) raporu, bankanın işlem bazlı kayıtları (genellikle brüt tutarı, komisyonu, net tutarı ve valör tarihini gösteren üye işyeri ekstresi veya mutabakat dosyası) ve banka hesap hareketleri. Gün sonu raporunun neden tek başına kanıt olmadığını " },
            { text: "kredi kartı gün sonu raporu ve banka mutabakatı", to: "/blog/kredi-karti-gun-sonu-raporu-banka-mutabakati/" },
            { text: " yazısında anlattık." },
          ],
        },
        { type: "heading", text: "POS toplamını banka tutarıyla adım adım karşılaştırma" },
        {
          type: "orderedList",
          items: [
            "Kart satışlarını işlem tarihine ve vardiyaya göre otomasyondan ve POS gün sonu raporundan alın.",
            "Her satışı bankanın işlem bazlı kaydıyla brüt tutar üzerinden eşleştirin: tutar, terminal ve saat.",
            "Ekstreden her işlemin komisyonunu ve net tutarını alın; brüt satıştan komisyon düşüldüğünde net tutar çıkmalıdır.",
            "Net tutarları valör tarihine göre gruplayın ve hesap hareketlerindeki alacak kayıtlarıyla karşılaştırın.",
            "İade, iptal, ters ibraz (harcama itirazı) ve diğer düzeltmeleri ayrı satırlarda gösterin.",
            "Bu adımlardan sonra hâlâ açıklanamayan farkı inceleme için işaretleyin.",
          ],
        },
        { type: "heading", text: "Örnek: 10.000 TL kart satışı, hesaba geçen 9.800 TL" },
        {
          type: "callout",
          tone: "example",
          title: "Örnek (temsili)",
          text: "Tutarlar yalnızca hesabı göstermek içindir; gerçek bir komisyon oranını veya süreyi temsil etmez. Bir vardiyada POS'ta 10.000 TL kart satışı yapıldığını düşünün. Banka ekstresinde bu satışlar için 200 TL komisyon görünüyor ve daha sonraki bir valör gününde hesaba 9.800 TL geçiyor. Aradaki 200 TL, ekstredeki komisyonla birebir açıklandığı için rakamlar tutarlıdır; bu fark kendiliğinden bir kasa veya personel açığı değildir.",
        },
        {
          type: "table",
          table: {
            caption: "Temsili hesap: satıştan hesaba geçen tutara (tutarlar örnektir)",
            headers: ["Kalem", "Tutar", "Kaynak"],
            rows: [
              ["Kart satışları (brüt)", "10.000 TL", "Otomasyon, POS gün sonu"],
              ["Komisyon", "200 TL kesinti", "Banka ekstresi"],
              ["Hesaba geçen net tutar", "9.800 TL", "Hesap hareketi, valör günü"],
            ],
          },
        },
        {
          type: "paragraph",
          text: "Sonuç, karşılaştırdığınız döneme ve aradaki düzeltmelere göre değişir. Banka aynı valör gününde başka bir günün satışlarını da aktarırsa hesap hareketi 9.800 TL'den büyük görünür; dönemde bir iade varsa hesaba geçen tutar azalır. Blokajlı bir anlaşmada ise 9.800 TL hesapta henüz hiç görünmeyebilir. Bu farkların hiçbiri, ekstredeki satırlarla açıklanabildiği sürece açık değildir; açık ancak ekstreyle açıklanamayan bir fark için konuşulur.",
        },
        { type: "heading", text: "İstisnalar ve elle inceleme gereken durumlar" },
        {
          type: "list",
          items: [
            "Hafta sonu ve resmi tatiller valör gününü kaydırabilir.",
            "Banka birden fazla günün veya terminalin tutarını tek bir alacak kaydıyla aktarabilir; işlem bazlı ekstre olmadan bu toplam ayrıştırılamaz.",
            "Komisyonla açıklanamayan ya da ekstrede karşılığı olmayan bir fark elle incelenmelidir.",
            "Ters ibraz gibi sonradan gelen düzeltmeler, kapattığınız bir dönemi etkileyebilir.",
            "POS gün sonunun alındığı saat, bir işlemin hangi güne yazılacağını etkileyebilir.",
            "Brüt ve net karıştırılmamalıdır: satış eşleştirmesi brüt işlem tutarıyla, hesap mutabakatı net tutarla yapılır.",
          ],
        },
        {
          type: "callout",
          tone: "limitation",
          title: "Dikkat edilmesi gerekenler",
          text: "Bu yazıdaki valör, blokaj, komisyon ve taksit açıklamaları geneldir; süreler, oranlar ve aktarım biçimi bankaya ve üye işyeri anlaşmasına göre değişir. Kendi koşullarınız için anlaşmanızı ve bankanızın ekstresini esas alın.",
        },
        { type: "heading", text: "Shift Tracker'da kart satışı doğrulama" },
        // Sources: blogPosts.ts kredi-karti-gun-sonu-raporu-banka-mutabakati tr (her kart satışı
        // bankanın gün sonu mutabakat dosyasıyla eşleştirilir), blogPosts.ts
        // shift-tracker-mutabakati-nasil-calisir tr ve landingPages.ts:131-132 (aynı tutar, aynı
        // terminal, dar zaman aralığı; tüm aktif bankaların dosyası beklenir, eksik resimle eşleştirme
        // yapılmaz), bu yazının önceki tr metni ve answers.ts when-card-sales-reach-bank (gün sonu
        // dosyaları çoğunlukla ertesi sabah; kart tarafı planlı çalışır), landingPages.ts:967 ve 972
        // (bankaya geçen satış kendiliğinden onaylanır; ulaşmayan veya tutmayan inceleme için öne çıkar)
        {
          type: "callout",
          tone: "product",
          title: "Shift Tracker bunu nasıl yapar?",
          segments: [
            { text: "Shift Tracker, otomasyondan (TURPAK, Asis) gelen her kart satışını bankanın gün sonu mutabakat dosyasındaki POS hareketiyle eşleştirir: aynı tutar, aynı terminal ve dar bir zaman aralığı. Bu dosyalar çoğunlukla ertesi sabah geldiği için kart tarafı mutabakatı anlık değil, planlı çalışır; bir günün mutabakatı o güne ait tüm aktif bankaların dosyaları gelene kadar bekletilir ve eksik resimle eşleştirme yapılmaz. Bankaya geçen kart satışları kendiliğinden onaylanır; bankaya hiç ulaşmamış ya da tutarı tutmayan bir satış inceleme için öne çıkarılır. Ayrıntılar: " },
            { text: "kredi kartı ödeme doğrulama", to: "/kredi-karti-odeme-dogrulama/" },
            { text: " ve " },
            { text: "Shift Tracker mutabakatı nasıl çalışır?", to: "/blog/shift-tracker-mutabakati-nasil-calisir/" },
          ],
        },
        {
          type: "paragraph",
          segments: [
            { text: "Özetle, POS toplamının aynı gün banka tutarına eşit olmaması normaldir. Önemli olan, her satışın doğru gün ve doğru tutarla bankaya eşleştiğinin ve aradaki farkın ekstreyle açıklandığının kanıtlanmasıdır. Vardiya farklarının nereden geldiğini " },
            { text: "Z raporu ile vardiya raporu neden tutmaz", to: "/blog/z-raporu-vardiya-raporu-neden-tutmaz/" },
            { text: " yazısında da anlattık. İstasyonunuzda bunun nasıl çalıştığını görmek için bizimle iletişime geçin." },
          ],
        },
      ],
    },
    en: {
      title: "When Do Card Sales Reach the Bank? A Guide to Settlement Timing",
      seoTitle: "When Do Card Sales Reach the Bank? - Shift Tracker",
      description:
        "Why isn't the card sale in your account the same day? How value date, holds, commission and installments separate the POS total from the bank amount.",
      modifiedAt: "2026-11-04",
      body: [
        {
          type: "paragraph",
          text: "You made the card sale, so why is the money not in the account the same day? This is not an error; it is how card payments reach the bank. This article explains how the value date, holds, commission and installments separate the POS total from the bank amount.",
        },
        {
          type: "callout",
          tone: "answer",
          title: "Short answer",
          text: "Card sale money reaches the account on the bank's value date, not at the moment of sale; the value date and any hold period depend on the bank and your merchant agreement. The amount credited is also net of commission, refunds and similar deductions. So a POS total that does not equal the bank amount on the same day is not a shortage, as long as the bank statement explains the difference.",
        },
        { type: "heading", text: "What value date, hold and commission mean" },
        {
          type: "paragraph",
          text: "A card transaction is approved at the POS instantly, but the bank's transaction record and the transfer of money to your account are separate steps. A shift's card sales usually only appear on the bank side the following day; the day the money is credited depends on your agreement.",
        },
        {
          type: "list",
          items: [
            "Value date (valör): the day a transaction actually credits the account and the amount becomes usable.",
            "Hold (blokaj): the period the amount is kept before it is transferred. Some agreements have no hold, others apply one for a set period; the length depends on the bank and the agreement.",
            "Commission: the fee the bank deducts from transactions; what lands is the gross sale minus commission. The rate depends on the bank and the agreement.",
            "Installments: if a transaction is paid in installments, whether the amount is transferred at once or in parts depends on the agreement.",
            "Refunds and cancellations: later refunds or cancellations change the amount credited and usually show as separate lines on the statement.",
          ],
        },
        {
          type: "paragraph",
          segments: [
            { text: "The inputs you need: the card sales in the automation's shift report, the POS end-of-day (Z) report, the bank's transaction-level records (the merchant statement or settlement file, usually showing the gross amount, commission, net amount and value date) and the bank account movements. Why the end-of-day report alone is not proof is covered in " },
            { text: "does your POS end-of-day report match the bank?", to: "/en/blog/pos-end-of-day-report-vs-bank-settlement/" },
          ],
        },
        { type: "heading", text: "Comparing the POS total with the bank amount, step by step" },
        {
          type: "orderedList",
          items: [
            "Take the card sales by transaction date and shift from the automation and the POS end-of-day report.",
            "Match each sale to the bank's transaction-level record on the gross amount: amount, terminal and time.",
            "Take each transaction's commission and net amount from the statement; the gross sale minus commission should give the net amount.",
            "Group the net amounts by value date and compare them with the credits in your account movements.",
            "Show refunds, cancellations, chargebacks and other adjustments on separate lines.",
            "Flag any difference that is still unexplained after these steps for review.",
          ],
        },
        { type: "heading", text: "Example: TRY 10,000 in card sales, TRY 9,800 credited" },
        {
          type: "callout",
          tone: "example",
          title: "Example (illustrative)",
          text: "The amounts only show the calculation; they do not represent a real commission rate or period. Say a shift ran TRY 10,000 in card sales on the POS. The bank statement shows a TRY 200 commission on those sales, and TRY 9,800 is credited to the account on a later value date. The TRY 200 gap is fully explained by the commission on the statement, so the figures are consistent; the gap is not, by itself, a cash or employee shortage.",
        },
        {
          type: "table",
          table: {
            caption: "Illustrative calculation: from sale to amount credited (example amounts)",
            headers: ["Item", "Amount", "Source"],
            rows: [
              ["Card sales (gross)", "TRY 10,000", "Automation, POS end-of-day"],
              ["Commission", "TRY 200 deducted", "Bank statement"],
              ["Net amount credited", "TRY 9,800", "Account movement, value date"],
            ],
          },
        },
        {
          type: "paragraph",
          text: "The result depends on the period you compare and the adjustments in between. If the bank credits another day's sales on the same value date, the account movement looks larger than TRY 9,800; if a refund falls in the period, the amount credited is lower. Under an agreement with a hold, the TRY 9,800 may not be in the account yet at all. None of these is a shortage as long as the statement lines explain it; a shortage only comes into question for a difference the statement cannot explain.",
        },
        { type: "heading", text: "Exceptions and when to review by hand" },
        {
          type: "list",
          items: [
            "Weekends and public holidays can move the value date.",
            "The bank may credit several days or terminals in a single entry; without a transaction-level statement, that total cannot be broken down.",
            "A difference that commission does not explain, or that has no line on the statement, needs a manual review.",
            "Later adjustments such as chargebacks can affect a period you have already closed.",
            "The time the POS end-of-day is taken can affect which day a transaction is booked to.",
            "Do not mix gross and net: the sale-level match uses the gross transaction amount, and the account reconciliation uses the net amount.",
          ],
        },
        {
          type: "callout",
          tone: "limitation",
          title: "Keep in mind",
          text: "The explanations of value date, hold, commission and installments here are general; periods, rates and how the money is transferred depend on the bank and your merchant agreement. For your own terms, rely on your agreement and your bank's statement.",
        },
        { type: "heading", text: "Card sale verification in Shift Tracker" },
        // Sources: blogPosts.ts pos-end-of-day-report-vs-bank-settlement en (every card sale matched
        // to the bank's end-of-day settlement file), blogPosts.ts how-shift-tracker-reconciliation-works
        // en and landingPages.ts:281-282 (same amount, same terminal, narrow time window; waits for
        // every active bank's file, never matches against an incomplete picture), this article's
        // previous en text and answers.ts when-card-sales-reach-bank (end-of-day file usually lands
        // the next morning; card side runs on a schedule), landingPages.ts:1044 and 1049 (settled
        // sales approved on their own; unsettled or mismatched sales surfaced for review)
        {
          type: "callout",
          tone: "product",
          title: "What Shift Tracker does",
          segments: [
            { text: "Shift Tracker matches every card sale from your automation (TURPAK, Asis) to the POS transaction in the bank's end-of-day settlement file: the same amount, the same terminal and a narrow time window. Because those files usually land the next morning, card-side reconciliation runs on a schedule rather than instantly; a day's reconciliation is held until every active bank's file for that day has arrived, so nothing is matched against an incomplete picture. Card sales that settle to the bank are approved on their own, and a sale that never reached the bank, or whose amount does not match, is surfaced for review. More on " },
            { text: "credit card payment verification", to: "/en/credit-card-payment-verification/" },
            { text: " and " },
            { text: "how Shift Tracker reconciliation works", to: "/en/blog/how-shift-tracker-reconciliation-works/" },
            { text: "." },
          ],
        },
        {
          type: "paragraph",
          segments: [
            { text: "In short, it is normal for the POS total not to equal the bank amount on the same day. What matters is proving that every sale matched the bank on the right day and for the right amount, and that the statement explains the gap. We also cover where shift differences come from in " },
            { text: "why your Z-report doesn't match the shift report", to: "/en/blog/why-z-report-doesnt-match-shift-report/" },
            { text: ". Contact us to see how this works on your own station." },
          ],
        },
      ],
    },
  },
  {
    slug: "z-raporu-vardiya-raporu-neden-tutmaz",
    enSlug: "why-z-report-doesnt-match-shift-report",
    publishedAt: "2026-07-13",
    tr: {
      title: "Z Raporu ile Vardiya Raporu Neden Tutmaz? (ve Nasıl Çözülür)",
      seoTitle: "Z Raporu ile Vardiya Raporu Neden Tutmaz? - Shift Tracker",
      description:
        "Z raporu gün sonunda kesilir, vardiyalarla çakışmaz, ödeme tipleri karışır; kasa bu yüzden tutmaz. Nedeni ve vardiya bazlı mutabakatla çözümü anlatıyoruz.",
      body: [
        {
          type: "paragraph",
          text: "Z raporunuz kasayla ya da vardiya raporuyla bir türlü tutmuyor mu? Yalnız değilsiniz. Bunun nedeni çoğu zaman bir hata değil, raporların hangi zaman aralığını kapsadığıdır. Bu yazıda farkın nereden geldiğini ve nasıl çözüldüğünü anlatıyoruz.",
        },
        { type: "heading", text: "Kısa cevap: Z raporu gün sonunda kesilir, vardiya sınırlarıyla çakışmaz" },
        {
          type: "paragraph",
          text: "Z raporu genellikle gün sonunda (ör. 23:59) alınır ve o güne ait tüm işlemleri tek bir paket olarak toplar. Vardiyalar ise gün içinde değişir. Bu nedenle bir vardiyanın satışı ile bir günün Z raporu aynı zaman aralığını kapsamaz ve tutarlar doğal olarak kaymış görünür.",
        },
        { type: "heading", text: "Z raporu, X raporu ve vardiya raporu arasındaki fark" },
        {
          type: "paragraph",
          text: "X raporu, gün içinde alınan ve kasayı sıfırlamayan bir ara özettir. Z raporu, günü kapatan ve toplamı sıfırlayan gün sonu raporudur. Vardiya raporu ise tek bir personelin çalıştığı süreyi kapsar. Üçü farklı zaman aralıklarına baktığı için birebir eşleşmeleri beklenmez.",
        },
        { type: "heading", text: "Z raporu neden kasayla ve vardiyayla tutmaz?" },
        {
          type: "list",
          items: [
            "Gün kayması: Z raporu gün sonunda kesilir, vardiya ise gün ortasında biter.",
            "Ödeme tipi karışıklığı: Kartla ödenen bir satış nakit girilmiş olabilir; kasa tutmaz.",
            "Vardiya ortası nakit: Devir sırasında alınan nakit hangi vardiyaya yazıldığına göre değişir.",
            "İade zamanlaması: Sonradan yapılan iadeler, ait olmadıkları vardiyada görünebilir.",
          ],
        },
        { type: "heading", text: "Farkı her seferinde elle çözmek yerine" },
        {
          type: "paragraph",
          segments: [
            { text: "Çözüm, tek bir kağıda güvenmek değil, iki bağımsız kaynağı, yani otomasyon satışlarını ve banka kayıtlarını her vardiya bazında eşleştirmektir. " },
            { text: "Mutabakatın nasıl çalıştığını", to: "/blog/shift-tracker-mutabakati-nasil-calisir/" },
            { text: " anlatan yazımızda bu iki-kaynak yönteminin neden doğru sonucu verdiğini görebilirsiniz." },
          ],
        },
        {
          type: "paragraph",
          segments: [
            { text: "Bu yaklaşımla vardiya, siz açtığınızda çoktan mutabık kılınmış olur; kasanın neden tutmadığını saatlerce aramak yerine " },
            { text: "vardiyayı bir dakikanın altında kapatırsınız", to: "/blog/vardiyayi-bir-dakikada-nasil-kapatirsiniz/" },
            { text: "." },
          ],
        },
        {
          type: "paragraph",
          text: "Özetle, Z raporunun vardiyayla tutmaması bir arıza değil, farklı zaman aralıklarının doğal sonucudur. Doğru çözüm, her vardiyayı banka onaylı kayıtlarla mutabık kılmaktır. İstasyonunuzda bunun nasıl çalıştığını görmek için bizimle iletişime geçin.",
        },
      ],
    },
    en: {
      title: "Why Your Z-Report Doesn't Match the Shift Report (and How to Fix It)",
      seoTitle: "Why Z-Reports Don't Match Shift Reports - Shift Tracker",
      description:
        "The Z-report is cut at day's end, not per shift, and payment types get mixed, so the register never balances. Here's how per-shift reconciliation fixes it.",
      body: [
        {
          type: "paragraph",
          text: "Does your Z-report never quite match the register or the shift report? You are not alone. The cause is usually not an error but which time window each report covers. This article explains where the difference comes from and how it is resolved.",
        },
        { type: "heading", text: "Short answer: the Z-report is cut at day end, not at shift boundaries" },
        {
          type: "paragraph",
          text: "The Z-report is usually taken at the end of the day (for example 23:59) and totals all of that day's transactions in a single batch. Shifts, however, change during the day. So a shift's sales and a day's Z-report do not cover the same time window, and the totals naturally look shifted.",
        },
        { type: "heading", text: "The difference between the Z-report, X-report and shift report" },
        {
          type: "paragraph",
          text: "The X-report is an interim summary taken during the day that does not reset the register. The Z-report is the end-of-day report that closes the day and resets the total. The shift report covers a single employee's time on duty. Because all three look at different time windows, they are not expected to match exactly.",
        },
        { type: "heading", text: "Why the Z-report does not match the register and the shift" },
        {
          type: "list",
          items: [
            "Day shift: the Z-report is cut at day end, while a shift ends in the middle of the day.",
            "Payment-type mix-up: a card sale may have been entered as cash; the register will not balance.",
            "Mid-shift cash: cash taken during handover depends on which shift it was recorded against.",
            "Refund timing: later refunds can appear in a shift they do not belong to.",
          ],
        },
        { type: "heading", text: "Instead of resolving the gap by hand every time" },
        {
          type: "paragraph",
          segments: [
            { text: "The fix is not to trust a single piece of paper, but to match two independent sources, the automation sales and the bank records, on a per-shift basis. Our article on " },
            { text: "how reconciliation works", to: "/en/blog/how-shift-tracker-reconciliation-works/" },
            { text: " shows why this two-source method produces the right result." },
          ],
        },
        {
          type: "paragraph",
          segments: [
            { text: "With this approach the shift is already reconciled by the time you open it; instead of hunting for hours why the register does not balance, you " },
            { text: "close a shift in under a minute", to: "/en/blog/how-to-close-a-shift-in-one-minute/" },
            { text: "." },
          ],
        },
        {
          type: "paragraph",
          text: "In short, a Z-report not matching the shift is not a fault but the natural result of different time windows. The right fix is to reconcile every shift against bank-confirmed records. Contact us to see how this works on your own station.",
        },
      ],
    },
  },
];

export function getPostBySlug(slug?: string): BlogPost | undefined {
  return blogPosts.find((post) => post.slug === slug);
}

/** Resolve a blog post by the URL slug for the active language. */
export function getPostForLang(
  slug: string | undefined,
  isEnglish: boolean
): BlogPost | undefined {
  return blogPosts.find((p) => (isEnglish ? p.enSlug : p.slug) === slug);
}

export function getSortedPosts(): BlogPost[] {
  return [...blogPosts].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
}

/** Last genuine content change of a language version (falls back to publication). */
export function getModifiedAt(post: BlogPost, lang: BlogLang): string {
  return post[lang].modifiedAt ?? post.publishedAt;
}

/**
 * Canonical URL for a post in the given language (trailing slash, /en prefix
 * and enSlug for English). Single home for the slug-vs-enSlug URL contract:
 * use this instead of hand-building post hrefs.
 */
export function getPostHref(post: BlogPost, lang: BlogLang): string {
  return lang === "en" ? `/en/blog/${post.enSlug}/` : `/blog/${post.slug}/`;
}

/** Shared "read more" link label, used by the blog index and related posts. */
export const readMoreLabel: Record<BlogLang, string> = {
  tr: "Devamını oku",
  en: "Read more",
};

export function formatBlogDate(date: string, lang: BlogLang): string {
  try {
    // Post dates are date-only ISO strings, parsed as UTC midnight. Format in
    // UTC too, or visitors west of UTC see the previous day - and the client
    // render then disagrees with the UTC build-time prerender (hydration
    // mismatch).
    return new Intl.DateTimeFormat(lang === "en" ? "en-US" : "tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    }).format(new Date(date));
  } catch {
    return date;
  }
}
